import { createIdempotencyKey, EpicMobileApi } from "./api";
import { createMobileGuestTransport } from "./guestChat";
import { MOBILE_GUEST_ENABLED } from "./environment";
import type { GuestResult, GuestView } from "../../public/shared/companion/guestChatTransport.js";
import { normalizePublicProfile } from "./accountProfile";
import {
  DEFAULT_MOBILE_UI,
  MOBILE_COMPANION_FIELD_OPTIONS,
  MOBILE_SOCIAL_PLATFORM_OPTIONS,
  normalizeMobileUiConfig
} from "./uiConfig";
import type {
  AccountAiPolicyAcceptance,
  AccountAiProfileSettings,
  AccountAiProfileSettingsResponse,
  AccountSummary,
  CheckinPreferences,
  CheckinSlotId,
  InboxConversation,
  InboxContactContextResponse,
  InboxComposerMode,
  InboxDraftRejectionReason,
  InboxFilters,
  InboxFolder,
  InboxFolderId,
  InboxLabel,
  InboxListItem,
  InboxMessage,
  InboxSort,
  InboxStatusFilter,
  LoadState,
  MessagingReviewDraft,
  MobileProfileView,
  NativeBridge,
  MobileSession,
  MobileTab,
  MobileUiCompanionFieldKey,
  MobileUiConfig,
  PageInfo,
  ProviderConnectionActionName,
  ProviderConnectionView,
  PublicAccountProfile,
  SocialLink,
  WynterProfileResponse,
  SafeError,
  WctekChatMessage,
  WctekChatSession,
  WctekChatThread,
  WctekServiceState,
  WctekSessionListResponse,
  WctekWorkspaceResponse
} from "./types";

export interface WctekChatState {
  loadState: LoadState;
  historyState: LoadState;
  sendState: LoadState;
  sessions: WctekChatSession[];
  threadId: string | null;
  sessionId: string | null;
  messages: WctekChatMessage[];
  composer: string;
  pendingClientMessageId: string | null;
  service: WctekServiceState | null;
  error: SafeError | null;
  historyError: SafeError | null;
  sendError: SafeError | null;
}

export interface CompanionSettingsDraft {
  aiName: string;
  basePersonality: string;
  communicationStyle: string;
  hardRefusals: string;
  softBoundaries: string;
  relationshipBoundaries: string;
  aiReminderFrequency: string;
  redirectToRealLife: string;
  updatedAt: string | null;
}

export interface CompanionSettingsState {
  loadState: LoadState;
  saveState: LoadState;
  saved: CompanionSettingsDraft | null;
  draft: CompanionSettingsDraft | null;
  policy: AccountAiPolicyAcceptance | null;
  policyAcceptedByUser: boolean;
  dirty: boolean;
  error: SafeError | null;
  saveError: SafeError | null;
}

export interface SocialLinksState {
  loadState: LoadState;
  saveState: LoadState;
  enabled: boolean;
  savedEnabled: boolean;
  saved: SocialLink[];
  draft: SocialLink[];
  revision: number;
  csrfToken: string;
  dirty: boolean;
  error: SafeError | null;
  saveError: SafeError | null;
}

export interface CheckinSettingsDraft {
  frequency: number;
  timezone: string;
  slots: Array<{ id: CheckinSlotId; time: string; enabled: boolean }>;
  quietHours: { enabled: boolean; start: string; end: string };
  pausedUntil: string;
  updatedAt: string | null;
}

export interface CheckinSettingsState {
  loadState: LoadState;
  saveState: LoadState;
  saved: CheckinSettingsDraft | null;
  draft: CheckinSettingsDraft | null;
  dirty: boolean;
  error: SafeError | null;
  saveError: SafeError | null;
}

export interface ReviewDraftsState {
  loadState: LoadState;
  saveState: LoadState;
  drafts: MessagingReviewDraft[];
  selectedDraftId: string | null;
  savedText: string;
  draftText: string;
  dirty: boolean;
  error: SafeError | null;
  saveError: SafeError | null;
}

export interface MemoryHydrationState {
  loadState: LoadState;
  saveState: LoadState;
  uploadState: LoadState;
  data: WynterProfileResponse | null;
  savedConsent: { styleLearningEnabled: boolean; conversationalUploadEnabled: boolean } | null;
  draftConsent: { styleLearningEnabled: boolean; conversationalUploadEnabled: boolean } | null;
  selectedFile: File | null;
  uploadConsent: boolean;
  uploadStatus: string | null;
  dirty: boolean;
  error: SafeError | null;
  saveError: SafeError | null;
  uploadError: SafeError | null;
}

export interface ProviderConnectionsState {
  loadState: LoadState;
  actionState: LoadState;
  connections: ProviderConnectionView[];
  activeActionKey: string | null;
  status: string | null;
  error: SafeError | null;
  actionError: SafeError | null;
}

export interface MobileAuthState {
  stage: "credentials" | "two-factor";
  username: string;
  submitState: LoadState;
  configState: LoadState;
  googleAvailable: boolean | null;
  googleClientId: string | null;
  googleNonce: string | null;
  challengeExpiresAt: string | null;
  error: SafeError | null;
}

interface InboxState {
  listState: LoadState;
  metadataState: LoadState;
  detailState: LoadState;
  historyState: LoadState;
  historyError: SafeError | null;
  contextState: LoadState;
  conversations: InboxListItem[];
  selectedConversation: InboxConversation | null;
  contactContext: InboxContactContextResponse | null;
  messages: InboxMessage[];
  folders: InboxFolder[];
  labels: InboxLabel[];
  filters: InboxFilters;
  filterDraft: InboxFilters;
  filtersOpen: boolean;
  loadedConversationPages: number;
  loadedMessagePages: number;
  page: PageInfo | null;
  messagePage: PageInfo | null;
  historyCoverage: import("./types").InboxHistoryCoverage | null;
  liveReconciliation: { cursor: string; through: InboxMessage | null } | null;
  liveState: LoadState;
  liveError: SafeError | null;
  error: SafeError | null;
  metadataError: SafeError | null;
  detailError: SafeError | null;
  contextError: SafeError | null;
  historySynchronization: {
    state?: string | null;
    inProgress?: boolean;
    historyExhausted?: boolean;
    requestable?: boolean;
    retryable?: boolean;
  } | null;
  composerMode: InboxComposerMode;
  composerText: string;
  composerState: LoadState;
  composerError: SafeError | null;
  composerIdempotencyKey: string | null;
  composerStatus: string | null;
  draftReviewText: string;
  draftReviewDirty: boolean;
  draftReviewRevision: string | null;
  draftReviewReason: InboxDraftRejectionReason;
  draftReviewState: LoadState;
  draftReviewError: SafeError | null;
  draftReviewIdempotencyKey: string | null;
  draftReviewAction: InboxDraftReviewAction | null;
  draftReviewStatus: string | null;
  draftSendConfirmed: boolean;
}

type InboxDraftReviewAction = "approve" | "reject" | "send";

const DEFAULT_INBOX_FILTERS: InboxFilters = Object.freeze({
  search: "",
  channel: "all",
  status: "open",
  sort: "latest_inbound",
  folder: "all",
  labelIds: []
});

const INBOX_STATUS_FILTERS = new Set<InboxStatusFilter>(["open", "closed", "all"]);
const INBOX_SORTS = new Set<InboxSort>(["latest_inbound", "latest_message"]);
const INBOX_FOLDERS = new Set<InboxFolderId>(["all", "unassigned", "mine", "olivia", "approval", "attention", "closed"]);

function cloneInboxFilters(filters: InboxFilters = DEFAULT_INBOX_FILTERS): InboxFilters {
  return { ...filters, labelIds: [...filters.labelIds] };
}

function emptyInboxDraftReviewState() {
  return {
    draftReviewText: "",
    draftReviewDirty: false,
    draftReviewRevision: null,
    draftReviewReason: "NEEDS_CHANGES" as InboxDraftRejectionReason,
    draftReviewState: "idle" as LoadState,
    draftReviewError: null,
    draftReviewIdempotencyKey: null,
    draftReviewAction: null,
    draftReviewStatus: null,
    draftSendConfirmed: false
  };
}

export interface MobileAppState {
  guest: { loadState: LoadState; busy: boolean; composer: string; messages: GuestResult["thread"]["messages"]; view: GuestView | null; error: string | null };
  activeTab: MobileTab;
  profileView: MobileProfileView;
  publicProfile: { loadState: LoadState; data: PublicAccountProfile | null; error: SafeError | null };
  sessionState: LoadState;
  session: MobileSession | null;
  activeAccount: AccountSummary | null;
  auth: MobileAuthState;
  inbox: InboxState;
  wctek: WctekChatState;
  companion: CompanionSettingsState;
  socialLinks: SocialLinksState;
  checkins: CheckinSettingsState;
  reviewDrafts: ReviewDraftsState;
  memoryHydration: MemoryHydrationState;
  providerConnections: ProviderConnectionsState;
  online: boolean;
  accountVersion: number;
  toast: string | null;
  ui: MobileUiConfig;
  uiState: LoadState;
}

type Listener = (state: MobileAppState) => void;

function emptyPublicProfile(): MobileAppState["publicProfile"] {
  return { loadState: "idle", data: null, error: null };
}

function emptyInboxState(): InboxState {
  return {
    listState: "idle",
    metadataState: "idle",
    detailState: "idle",
    historyState: "idle",
    historyError: null,
    contextState: "idle",
    conversations: [],
    selectedConversation: null,
    contactContext: null,
    messages: [],
    folders: [],
    labels: [],
    filters: cloneInboxFilters(),
    filterDraft: cloneInboxFilters(),
    filtersOpen: false,
    loadedConversationPages: 0,
    loadedMessagePages: 0,
    page: null,
    messagePage: null,
    historyCoverage: null,
    liveReconciliation: null,
    liveState: "idle",
    liveError: null,
    error: null,
    metadataError: null,
    detailError: null,
    contextError: null,
    historySynchronization: null,
    composerMode: "reply",
    composerText: "",
    composerState: "idle",
    composerError: null,
    composerIdempotencyKey: null,
    composerStatus: null,
    ...emptyInboxDraftReviewState()
  };
}

function emptyWctekState(): WctekChatState {
  return {
    loadState: "idle",
    historyState: "idle",
    sendState: "idle",
    sessions: [],
    threadId: null,
    sessionId: null,
    messages: [],
    composer: "",
    pendingClientMessageId: null,
    service: null,
    error: null,
    historyError: null,
    sendError: null
  };
}

function emptyCompanionState(): CompanionSettingsState {
  return {
    loadState: "idle",
    saveState: "idle",
    saved: null,
    draft: null,
    policy: null,
    policyAcceptedByUser: false,
    dirty: false,
    error: null,
    saveError: null
  };
}

function emptySocialLinksState(): SocialLinksState {
  return {
    loadState: "idle",
    saveState: "idle",
    enabled: true,
    savedEnabled: true,
    saved: [],
    draft: [],
    revision: 0,
    csrfToken: "",
    dirty: false,
    error: null,
    saveError: null
  };
}

function emptyCheckinState(): CheckinSettingsState {
  return {
    loadState: "idle",
    saveState: "idle",
    saved: null,
    draft: null,
    dirty: false,
    error: null,
    saveError: null
  };
}

function emptyReviewDraftsState(): ReviewDraftsState {
  return {
    loadState: "idle",
    saveState: "idle",
    drafts: [],
    selectedDraftId: null,
    savedText: "",
    draftText: "",
    dirty: false,
    error: null,
    saveError: null
  };
}

function emptyMemoryHydrationState(): MemoryHydrationState {
  return {
    loadState: "idle",
    saveState: "idle",
    uploadState: "idle",
    data: null,
    savedConsent: null,
    draftConsent: null,
    selectedFile: null,
    uploadConsent: false,
    uploadStatus: null,
    dirty: false,
    error: null,
    saveError: null,
    uploadError: null
  };
}

function emptyProviderConnectionsState(): ProviderConnectionsState {
  return {
    loadState: "idle",
    actionState: "idle",
    connections: [],
    activeActionKey: null,
    status: null,
    error: null,
    actionError: null
  };
}

function emptyAuthState(): MobileAuthState {
  return {
    stage: "credentials",
    username: "",
    submitState: "idle",
    configState: "idle",
    googleAvailable: null,
    googleClientId: null,
    googleNonce: null,
    challengeExpiresAt: null,
    error: null
  };
}

function activeAccountFromSession(session: MobileSession | null): AccountSummary | null {
  return session?.identity?.activeAccount || session?.activeAccount || null;
}

function safeError(error: any): SafeError {
  return {
    status: Number(error?.status || 0),
    code: String(error?.code || "REQUEST_FAILED"),
    message: String(error?.message || "Request failed."),
    retryable: error?.retryable === true,
    requiredAction: error?.requiredAction || null,
    correlationRef: error?.correlationRef || null,
    data: error?.data
  };
}

function normalizedUsername(value: unknown) {
  return String(value || "").trim().toLowerCase();
}

function compactInput(value: unknown, max: number) {
  return String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u0000-\u0009\u000b\u000c\u000e-\u001f\u007f]/g, " ")
    .slice(0, max);
}

function allowedValue(value: unknown, allowed: readonly string[], fallback: string) {
  const normalized = String(value || "").trim().toLowerCase();
  return allowed.includes(normalized) ? normalized : fallback;
}

function listText(value: unknown) {
  if (!Array.isArray(value)) return "";
  return value
    .map((item) => compactInput(item, 180).replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .slice(0, 12)
    .join("\n");
}

function companionDraft(settings: AccountAiProfileSettings = {}): CompanionSettingsDraft {
  return {
    aiName: compactInput(settings.coreIdentity?.aiName, 80).trim(),
    basePersonality: compactInput(settings.coreIdentity?.basePersonality, 1200).trim(),
    communicationStyle: allowedValue(settings.coreIdentity?.communicationStyle, MOBILE_COMPANION_FIELD_OPTIONS["communication-style"]!, "warm"),
    hardRefusals: listText(settings.boundaries?.hardRefusals),
    softBoundaries: listText(settings.boundaries?.softBoundaries),
    relationshipBoundaries: allowedValue(settings.boundaries?.relationshipBoundaries, MOBILE_COMPANION_FIELD_OPTIONS["relationship-boundaries"]!, "non_sexual_non_possessive"),
    aiReminderFrequency: allowedValue(settings.boundaries?.aiReminderFrequency, MOBILE_COMPANION_FIELD_OPTIONS["ai-reminder-frequency"]!, "balanced"),
    redirectToRealLife: allowedValue(settings.boundaries?.redirectToRealLife, MOBILE_COMPANION_FIELD_OPTIONS["redirect-to-real-life"]!, "balanced"),
    updatedAt: settings.updatedAt ? String(settings.updatedAt) : null
  };
}

function sameDraft(left: CompanionSettingsDraft | null, right: CompanionSettingsDraft | null) {
  if (!left || !right) return left === right;
  return [
    "aiName",
    "basePersonality",
    "communicationStyle",
    "hardRefusals",
    "softBoundaries",
    "relationshipBoundaries",
    "aiReminderFrequency",
    "redirectToRealLife"
  ].every((key) => left[key as keyof CompanionSettingsDraft] === right[key as keyof CompanionSettingsDraft]);
}

function normalizedList(value: string) {
  return String(value || "")
    .split(/\r?\n|,/)
    .map((item) => item.replace(/\s+/g, " ").trim().slice(0, 180))
    .filter(Boolean)
    .slice(0, 12);
}

function companionPatch(saved: CompanionSettingsDraft, draft: CompanionSettingsDraft) {
  const coreIdentity: Record<string, unknown> = {};
  const boundaries: Record<string, unknown> = {};
  if (saved.aiName !== draft.aiName) coreIdentity.aiName = draft.aiName;
  if (saved.basePersonality !== draft.basePersonality) coreIdentity.basePersonality = draft.basePersonality;
  if (saved.communicationStyle !== draft.communicationStyle) coreIdentity.communicationStyle = draft.communicationStyle;
  if (saved.hardRefusals !== draft.hardRefusals) boundaries.hardRefusals = normalizedList(draft.hardRefusals);
  if (saved.softBoundaries !== draft.softBoundaries) boundaries.softBoundaries = normalizedList(draft.softBoundaries);
  if (saved.relationshipBoundaries !== draft.relationshipBoundaries) boundaries.relationshipBoundaries = draft.relationshipBoundaries;
  if (saved.aiReminderFrequency !== draft.aiReminderFrequency) boundaries.aiReminderFrequency = draft.aiReminderFrequency;
  if (saved.redirectToRealLife !== draft.redirectToRealLife) boundaries.redirectToRealLife = draft.redirectToRealLife;
  return {
    ...(Object.keys(coreIdentity).length ? { coreIdentity } : {}),
    ...(Object.keys(boundaries).length ? { boundaries } : {})
  };
}

function normalizedSocialLink(item: SocialLink, index: number): SocialLink {
  const platform = MOBILE_SOCIAL_PLATFORM_OPTIONS.includes(item?.platform as typeof MOBILE_SOCIAL_PLATFORM_OPTIONS[number])
    ? item.platform
    : "custom";
  return {
    id: compactInput(item?.id, 128).trim() || `mobile_social_${index + 1}`,
    platform,
    label: compactInput(item?.label, 80).trim(),
    url: compactInput(item?.url, 2048).trim(),
    visible: item?.visible !== false,
    imageAssetId: compactInput(item?.imageAssetId, 128).trim() || null,
    customPlatform: platform === "custom" && item?.customPlatform === true,
    platformName: compactInput(item?.platformName, 60).trim() || null,
    size: ["small", "medium", "large"].includes(String(item?.size)) ? item.size : "medium"
  };
}

function normalizedSocialLinks(items: unknown) {
  return (Array.isArray(items) ? items : [])
    .slice(0, 25)
    .map((item, index) => normalizedSocialLink(item as SocialLink, index));
}

function sameValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function newSocialLinkId() {
  const random = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "")
    : Math.random().toString(36).slice(2);
  return `mobile_social_${random}`.slice(0, 120);
}

const CHECKIN_SLOT_IDS: CheckinSlotId[] = ["morning", "afternoon", "evening"];
const DEFAULT_CHECKIN_TIMES: Record<CheckinSlotId, string> = {
  morning: "09:00",
  afternoon: "14:00",
  evening: "20:00"
};

function checkinInputDate(value: unknown) {
  const date = value ? new Date(String(value)) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function checkinDraft(preferences: CheckinPreferences): CheckinSettingsDraft {
  const byId = new Map((Array.isArray(preferences?.slots) ? preferences.slots : []).map((slot) => [slot.id, slot]));
  const slots = CHECKIN_SLOT_IDS.map((id, index) => {
    const slot = byId.get(id);
    return {
      id,
      time: /^([01]\d|2[0-3]):[0-5]\d$/.test(String(slot?.time || "")) ? String(slot?.time) : DEFAULT_CHECKIN_TIMES[id],
      enabled: typeof slot?.enabled === "boolean" ? slot.enabled : index < Math.max(0, Math.min(3, Number(preferences?.frequency || 0)))
    };
  });
  return {
    frequency: slots.filter((slot) => slot.enabled).length,
    timezone: compactInput(preferences?.timezone || "UTC", 80).trim() || "UTC",
    slots,
    quietHours: {
      enabled: preferences?.quietHours?.enabled === true,
      start: /^([01]\d|2[0-3]):[0-5]\d$/.test(String(preferences?.quietHours?.start || "")) ? preferences.quietHours.start : "22:00",
      end: /^([01]\d|2[0-3]):[0-5]\d$/.test(String(preferences?.quietHours?.end || "")) ? preferences.quietHours.end : "08:00"
    },
    pausedUntil: checkinInputDate(preferences?.pausedUntil),
    updatedAt: preferences?.updatedAt ? String(preferences.updatedAt) : null
  };
}

function checkinPatch(saved: CheckinSettingsDraft, draft: CheckinSettingsDraft) {
  const patch: Record<string, unknown> = {};
  if (saved.timezone !== draft.timezone) patch.timezone = draft.timezone;
  if (!sameValue(saved.slots, draft.slots) || saved.frequency !== draft.frequency) {
    patch.frequency = draft.slots.filter((slot) => slot.enabled).length;
    patch.slots = draft.slots.map((slot) => ({ ...slot }));
  }
  if (!sameValue(saved.quietHours, draft.quietHours)) patch.quietHours = { ...draft.quietHours };
  if (saved.pausedUntil !== draft.pausedUntil) {
    patch.pausedUntil = draft.pausedUntil ? new Date(`${draft.pausedUntil}T23:59:59`).toISOString() : null;
  }
  return patch;
}

function consentFromProfile(data: WynterProfileResponse) {
  const consent = data.consent || {};
  return {
    styleLearningEnabled: consent.styleLearningEnabled === true || consent.style_learning_enabled === true,
    conversationalUploadEnabled: consent.conversationalUploadEnabled === true || consent.conversational_upload_enabled === true
  };
}

function assertAccountUsername(actual: unknown, account: AccountSummary, message: string, code: string) {
  if (!normalizedUsername(actual) || normalizedUsername(actual) !== normalizedUsername(account.username)) {
    throw Object.assign(new Error(message), { status: 403, code, retryable: false });
  }
}

const PROVIDER_ACTIONS: ProviderConnectionActionName[] = ["connect", "reconnect", "recheck", "verify-subscription", "disconnect"];

function boundedProviderText(value: unknown, max = 160) {
  return compactInput(value, max).replace(/\s+/g, " ").trim();
}

function normalizeProviderConnection(view: ProviderConnectionView, index: number): ProviderConnectionView | null {
  const targetRef = boundedProviderText(view?.targetRef, 160);
  if (!targetRef) return null;
  const label = boundedProviderText(view?.branding?.label, 80) || "Provider";
  const instagram = String(view?.platform || "").toLowerCase() === "instagram" || label.toLowerCase() === "instagram" || targetRef === "instagram";
  const textbee = String(view?.platform || "").toLowerCase() === "textbee";
  const providerTone = ({ positive: "success", caution: "warning", critical: "danger" } as Record<string, string>)[String(view?.state?.tone)] || view?.state?.tone;
  return {
    platform: boundedProviderText(view?.platform, 40) || null,
    targetRef,
    branding: {
      iconKey: boundedProviderText(view?.branding?.iconKey, 40) || null,
      label
    },
    display: {
      primary: boundedProviderText(view?.display?.primary, 100) || `${label} connection`,
      secondary: boundedProviderText(view?.display?.secondary, 220)
    },
    state: {
      label: boundedProviderText(view?.state?.label, 80) || "Status unavailable",
      tone: ["neutral", "accent", "success", "warning", "danger"].includes(String(providerTone)) ? providerTone : "neutral",
      message: boundedProviderText(view?.state?.message, 220)
    },
    indicators: (Array.isArray(view?.indicators) ? view.indicators : []).slice(0, 6).map((indicator) => ({
      label: boundedProviderText(indicator?.label, 60),
      display: boundedProviderText(indicator?.display, 100),
      state: boundedProviderText(indicator?.state, 30)
    })),
    actions: (Array.isArray(view?.actions) ? view.actions : [])
      .filter((action) => PROVIDER_ACTIONS.includes(action?.name as ProviderConnectionActionName))
      .filter((action) => !["connect", "reconnect"].includes(action.name) || instagram || textbee)
      .filter((action) => action.name !== "disconnect" || textbee)
      .slice(0, 4)
      .map((action) => ({
        name: action.name,
        label: boundedProviderText(action.label, 80) || readableProviderAction(action.name),
        enabled: action.enabled === true,
        confirmation: action.confirmation?.required === true ? {
          required: true,
          title: boundedProviderText(action.confirmation.title, 100),
          message: boundedProviderText(action.confirmation.message, 220)
        } : null
      }))
  };
}

function readableProviderAction(action: unknown) {
  const text = String(action || "").replace(/-/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Continue";
}

function assertCompanionAccount(response: AccountAiProfileSettingsResponse, account: AccountSummary) {
  const expected = normalizedUsername(account.username);
  const settingsAccount = normalizedUsername(response.settings?.accountUsername);
  const contextAccount = normalizedUsername(response.accountContext?.account?.username);
  if (!expected || !settingsAccount || settingsAccount !== expected || (contextAccount && contextAccount !== expected)) {
    throw Object.assign(new Error("Companion settings were returned for a different EPIC account."), {
      status: 403,
      code: "COMPANION_ACCOUNT_MISMATCH",
      retryable: false
    });
  }
}

const WCTEK_SESSION_ID_RE = /^[A-Za-z0-9._:-]{8,120}$/;

function wctekContextError(message: string, code = "WCTEK_CONTEXT_MISMATCH") {
  return Object.assign(new Error(message), {
    status: 403,
    code,
    retryable: false
  });
}

function assertWctekThread(thread: WctekChatThread | null | undefined, account: AccountSummary) {
  if (!thread || normalizedUsername(thread.activeAccountUsername) !== normalizedUsername(account.username)) {
    throw wctekContextError("WCTEK AI returned a conversation for a different EPIC account.", "WCTEK_ACCOUNT_MISMATCH");
  }
  if (normalizedUsername(thread.companionUsername) !== "ctek" || !WCTEK_SESSION_ID_RE.test(String(thread.sessionId || ""))) {
    throw wctekContextError("WCTEK AI returned an invalid conversation context.");
  }
  if (!WCTEK_SESSION_ID_RE.test(String(thread.threadId || ""))) {
    throw wctekContextError("WCTEK AI returned an invalid thread context.");
  }
}

function assertWctekSessionList(response: WctekSessionListResponse, account: AccountSummary) {
  if (normalizedUsername(response.activeAccountUsername) !== normalizedUsername(account.username)) {
    throw wctekContextError("WCTEK AI returned conversation history for a different EPIC account.", "WCTEK_ACCOUNT_MISMATCH");
  }
}

function normalizedWctekAgents(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 3).map((agent: any) => ({
    id: compactInput(agent?.id, 60).trim(),
    displayName: compactInput(agent?.displayName, 80).trim()
  })).filter((agent) => agent.id && agent.displayName);
}

function normalizedWctekMessages(value: unknown): WctekChatMessage[] {
  if (!Array.isArray(value)) return [];
  return value.slice(-50).map((message: any) => ({
    role: message?.role === "assistant" ? "assistant" : "user",
    clientMessageId: compactInput(message?.clientMessageId, 120).trim() || null,
    messageId: compactInput(message?.messageId, 120).trim() || null,
    replyId: compactInput(message?.replyId, 120).trim() || null,
    text: compactInput(message?.text, 8000),
    createdAt: compactInput(message?.createdAt, 80).trim() || null,
    origin: compactInput(message?.origin, 60).trim() || null,
    agent: message?.agent ? {
      id: compactInput(message.agent.id, 60).trim(),
      displayName: compactInput(message.agent.displayName, 80).trim()
    } : null,
    orchestration: message?.orchestration ? {
      runId: compactInput(message.orchestration.runId, 120).trim() || null,
      selectedAgents: normalizedWctekAgents(message.orchestration.selectedAgents),
      specialistAgents: normalizedWctekAgents(message.orchestration.specialistAgents),
      routeReason: compactInput(message.orchestration.routeReason, 300).trim() || null,
      reviewStatus: compactInput(message.orchestration.reviewStatus, 80).trim() || null
    } : null
  })).filter((message) => message.text.trim());
}

function normalizedWctekSessions(value: unknown): WctekChatSession[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 50).map((session: any) => ({
    sessionId: compactInput(session?.sessionId, 120).trim(),
    title: compactInput(session?.title, 72).trim() || "Conversation",
    messageCount: Math.max(0, Math.min(10000, Number(session?.messageCount) || 0)),
    createdAt: compactInput(session?.createdAt, 80).trim() || null,
    updatedAt: compactInput(session?.updatedAt, 80).trim() || null
  })).filter((session) => WCTEK_SESSION_ID_RE.test(session.sessionId));
}

function normalizedWctekService(value: WctekServiceState | null | undefined): WctekServiceState {
  const status = ["online", "disabled", "connecting"].includes(String(value?.status)) ? String(value?.status) : "disabled";
  return { status, capabilities: { chat: status === "online" && value?.capabilities?.chat === true } };
}

function wctekThreadPatch(response: WctekWorkspaceResponse | { thread: WctekChatThread }, account: AccountSummary) {
  assertWctekThread(response.thread, account);
  return {
    threadId: response.thread.threadId,
    sessionId: response.thread.sessionId || null,
    messages: normalizedWctekMessages(response.thread.messages)
  };
}

function compareMessages(left: InboxMessage, right: InboxMessage) {
  const timestamp = (message: InboxMessage) => {
    const parsed = new Date(message.occurredAt || 0).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return timestamp(left) - timestamp(right) || left.messageRef.localeCompare(right.messageRef);
}

function mergeMessages(existing: InboxMessage[], incoming: InboxMessage[]) {
  const byRef = new Map<string, InboxMessage>();
  for (const message of [...existing, ...incoming]) {
    if (message.messageRef) byRef.set(message.messageRef, message);
  }
  return [...byRef.values()].sort(compareMessages);
}

export function createMobileStore(
  api = new EpicMobileApi(),
  options: {
    guestStorage?: Storage;
    googleCredentialProvider?: Pick<NativeBridge, "supportsNativeGoogleSignIn" | "signInWithGoogle">;
    requestProviderSetup?: typeof import("../../public/shared/manager/textBeeGatewaySetup").requestTextBeeSetup;
  } = {}
) {
  let listener: Listener = () => {};
  let state: MobileAppState = {
    guest: { loadState: "idle", busy: false, composer: "", messages: [], view: null, error: null },
    activeTab: "home",
    profileView: "public",
    publicProfile: emptyPublicProfile(),
    sessionState: "loading",
    session: null,
    activeAccount: null,
    auth: emptyAuthState(),
    inbox: emptyInboxState(),
    wctek: emptyWctekState(),
    companion: emptyCompanionState(),
    socialLinks: emptySocialLinksState(),
    checkins: emptyCheckinState(),
    reviewDrafts: emptyReviewDraftsState(),
    memoryHydration: emptyMemoryHydrationState(),
    providerConnections: emptyProviderConnectionsState(),
    online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
    accountVersion: 0,
    toast: null,
    ui: DEFAULT_MOBILE_UI,
    uiState: "idle"
  };
  const controllers = new Map<string, AbortController>();
  // Disposable drafts remain bound to the account version and exact thread.
  // Neither messages nor drafts are persisted in browser storage.
  const inboxDrafts = new Map<string, Partial<InboxState>>();
  let liveInboxRequest: Promise<void> | null = null;

  function rememberInboxDraft() {
    const reference = state.inbox.selectedConversation?.conversationRef;
    if (!reference) return;
    const inbox = state.inbox;
    const key = `${state.accountVersion}:${reference}`;
    if (!inbox.composerText && !inbox.draftReviewDirty) { inboxDrafts.delete(key); return; }
    inboxDrafts.set(key, {
      composerMode: inbox.composerMode, composerText: inbox.composerText,
      composerIdempotencyKey: inbox.composerIdempotencyKey,
      draftReviewText: inbox.draftReviewText, draftReviewDirty: inbox.draftReviewDirty,
      draftReviewRevision: inbox.draftReviewRevision, draftReviewReason: inbox.draftReviewReason
    });
  }
  let authConfigRequest: Promise<void> | null = null;
  let guestTransport: ReturnType<typeof createMobileGuestTransport> | null = null;
  const guestClient = () => guestTransport ||= createMobileGuestTransport(api, options.guestStorage);

  function setGuest(patch: Partial<MobileAppState["guest"]>) {
    setState({ guest: { ...state.guest, ...patch } });
  }

  async function loadGuest(recover = false) {
    if (!MOBILE_GUEST_ENABLED) return;
    if (state.sessionState !== "unauthenticated" || state.guest.busy || !state.online) return;
    setGuest({ busy: true, error: null });
    try {
      const result = await (recover ? guestClient().recover() : guestClient().load());
      setGuest({ messages: result.thread.messages, view: result.view, loadState: "ready" });
    } catch (error: any) {
      setGuest({ view: guestTransport?.view() || null, loadState: "error", error: safeError(error).message });
    } finally { setGuest({ busy: false }); }
  }

  async function sendGuest() {
    if (!MOBILE_GUEST_ENABLED) return;
    if (state.sessionState !== "unauthenticated" || state.guest.busy || !state.online) return;
    const message = state.guest.composer.trim();
    if (!message || guestClient().view().disabled) return;
    setGuest({ busy: true, error: null });
    try {
      // The shared transport persists this identity before the first send.
      const payload = guestClient().createRequest(message, crypto.randomUUID());
      const result = await guestClient().send(payload);
      setGuest({ messages: result.thread.messages, view: result.view, composer: "", loadState: "ready" });
    } catch (error: any) {
      // An interrupted response remains owned by the same request. Never make
      // another ID or resubmit automatically to recover an uncertain outcome.
      try {
        const result = await guestClient().load();
        setGuest({ messages: result.thread.messages, view: result.view,
          ...(result.view.disabled ? {} : { composer: "" }) });
      } catch { setGuest({ view: guestTransport?.view() || null }); }
      setGuest({ error: safeError(error).message });
    } finally { setGuest({ busy: false }); }
  }

  function refreshGuestDisplay() {
    if (state.sessionState !== "unauthenticated" || state.activeTab !== "olivia" || !guestTransport || state.guest.busy) return;
    const view = guestTransport.view();
    if (JSON.stringify(view) !== JSON.stringify(state.guest.view)) setGuest({ view });
  }

  function emit() {
    listener(state);
  }

  function setState(patch: Partial<MobileAppState>) {
    state = { ...state, ...patch };
    emit();
  }

  function setInbox(patch: Partial<InboxState>) {
    state = { ...state, inbox: { ...state.inbox, ...patch } };
    emit();
  }

  function setWctek(patch: Partial<WctekChatState>) {
    state = { ...state, wctek: { ...state.wctek, ...patch } };
    emit();
  }

  function setCompanion(patch: Partial<CompanionSettingsState>) {
    state = { ...state, companion: { ...state.companion, ...patch } };
    emit();
  }

  function setSocialLinks(patch: Partial<SocialLinksState>) {
    state = { ...state, socialLinks: { ...state.socialLinks, ...patch } };
    emit();
  }

  function setCheckins(patch: Partial<CheckinSettingsState>) {
    state = { ...state, checkins: { ...state.checkins, ...patch } };
    emit();
  }

  function setReviewDrafts(patch: Partial<ReviewDraftsState>) {
    state = { ...state, reviewDrafts: { ...state.reviewDrafts, ...patch } };
    emit();
  }

  function setMemoryHydration(patch: Partial<MemoryHydrationState>) {
    state = { ...state, memoryHydration: { ...state.memoryHydration, ...patch } };
    emit();
  }

  function setProviderConnections(patch: Partial<ProviderConnectionsState>) {
    state = { ...state, providerConnections: { ...state.providerConnections, ...patch } };
    emit();
  }

  function setAuth(patch: Partial<MobileAuthState>) {
    state = { ...state, auth: { ...state.auth, ...patch } };
    emit();
  }

  function abortRequest(key: string) {
    controllers.get(key)?.abort();
    controllers.delete(key);
  }

  function controller(key: string) {
    abortRequest(key);
    const next = new AbortController();
    controllers.set(key, next);
    return next;
  }

  function currentRequest(key: string, request: AbortController, version: number) {
    return version === state.accountVersion && controllers.get(key) === request;
  }

  function abortAccountScopedRequests() {
    inboxDrafts.clear();
    for (const key of [
      "inbox:list",
      "inbox:metadata",
      "inbox:detail",
      "inbox:history",
      "inbox:live",
      "inbox:list:live",
      "inbox:compose",
      "inbox:draft-review",
      "wctek:workspace",
      "wctek:history",
      "wctek:send",
      "companion:load",
      "companion:save",
      "social-links:load",
      "social-links:save",
      "profile:public",
      "checkins:load",
      "checkins:save",
      "review-drafts:load",
      "review-drafts:save",
      "memory:load",
      "memory:save",
      "memory:upload",
      "providers:load",
      "providers:action",
      "mobile:ui"
    ]) abortRequest(key);
  }

  function abortAuthenticationRequests() {
    for (const key of ["auth:config", "auth:login", "auth:google", "auth:two-factor"]) abortRequest(key);
  }

  function clearAccountScopedState() {
    inboxDrafts.clear();
    state = {
      ...state,
      accountVersion: state.accountVersion + 1,
      profileView: "public",
      publicProfile: emptyPublicProfile(),
      inbox: emptyInboxState(),
      wctek: emptyWctekState(),
      companion: emptyCompanionState(),
      socialLinks: emptySocialLinksState(),
      checkins: emptyCheckinState(),
      reviewDrafts: emptyReviewDraftsState(),
      memoryHydration: emptyMemoryHydrationState(),
      providerConnections: emptyProviderConnectionsState(),
      toast: null
    };
    emit();
  }

  function handleAccountSecurityError(error: SafeError) {
    if (error.status === 401) {
      abortAccountScopedRequests();
      state = {
        ...state,
        accountVersion: state.accountVersion + 1,
        sessionState: "unauthenticated",
        session: null,
        activeAccount: null,
        auth: emptyAuthState(),
        inbox: emptyInboxState(),
        wctek: emptyWctekState(),
        companion: emptyCompanionState(),
        socialLinks: emptySocialLinksState(),
        checkins: emptyCheckinState(),
        reviewDrafts: emptyReviewDraftsState(),
        memoryHydration: emptyMemoryHydrationState(),
        providerConnections: emptyProviderConnectionsState(),
        profileView: "public",
        publicProfile: emptyPublicProfile(),
        toast: null
      };
      emit();
      void loadAuthConfig();
      return true;
    }
    if ([
      "COMPANION_ACCOUNT_MISMATCH",
      "SOCIAL_LINKS_ACTIVE_ACCOUNT_CHANGED",
      "REVIEW_DRAFT_ACCOUNT_MISMATCH",
      "MEMORY_ACCOUNT_MISMATCH",
      "WCTEK_ACCOUNT_MISMATCH",
      "WCTEK_CONTEXT_MISMATCH"
    ].includes(error.code)) {
      abortAccountScopedRequests();
      state = {
        ...state,
        accountVersion: state.accountVersion + 1,
        sessionState: "error",
        session: null,
        activeAccount: null,
        profileView: "public",
        publicProfile: emptyPublicProfile(),
        inbox: emptyInboxState(),
        wctek: emptyWctekState(),
        companion: error.code === "COMPANION_ACCOUNT_MISMATCH"
          ? { ...emptyCompanionState(), loadState: "forbidden", error }
          : emptyCompanionState(),
        socialLinks: emptySocialLinksState(),
        checkins: emptyCheckinState(),
        reviewDrafts: emptyReviewDraftsState(),
        memoryHydration: emptyMemoryHydrationState(),
        providerConnections: emptyProviderConnectionsState(),
        toast: error.message
      };
      emit();
      return true;
    }
    return false;
  }

  function authenticationError(code: string, message: string): SafeError {
    return {
      status: 0,
      code,
      message,
      retryable: true,
      requiredAction: null,
      correlationRef: null
    };
  }

  async function loadAuthConfig() {
    if (authConfigRequest) return authConfigRequest;
    const request = refreshAuthConfig();
    authConfigRequest = request;
    try {
      await request;
    } finally {
      if (authConfigRequest === request) authConfigRequest = null;
    }
  }

  async function refreshAuthConfig() {
    const request = controller("auth:config");
    setAuth({ configState: "loading", googleClientId: null, googleNonce: null });
    try {
      const response = await api.getAuthConfig(request.signal);
      if (controllers.get("auth:config") !== request || state.sessionState !== "unauthenticated") return;
      const googleClientId = compactInput(response.google?.clientId, 256).trim();
      const googleNonce = compactInput(response.google?.nonce, 512).trim();
      setAuth({
        configState: "ready",
        googleAvailable: response.google?.enabled === true && Boolean(googleClientId && googleNonce),
        googleClientId: googleClientId || null,
        googleNonce: googleNonce || null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (controllers.get("auth:config") !== request || state.sessionState !== "unauthenticated") return;
      setAuth({
        configState: "error",
        googleAvailable: false,
        googleClientId: null,
        googleNonce: null
      });
    }
  }

  async function completeSignIn(response: { success: boolean; twoFactorRequired?: boolean; challengeExpiresAt?: string | null }) {
    if (response.twoFactorRequired === true) {
      setAuth({
        stage: "two-factor",
        submitState: "idle",
        challengeExpiresAt: response.challengeExpiresAt || null,
        googleClientId: null,
        googleNonce: null,
        error: null
      });
      return;
    }
    if (response.success !== true) {
      throw Object.assign(new Error("EPIC could not complete sign-in."), { code: "AUTH_RESPONSE_INVALID" });
    }
    abortAuthenticationRequests();
    state = { ...state, auth: emptyAuthState() };
    emit();
    await loadSession();
  }

  async function signIn(username: string, password: string) {
    if (state.auth.submitState === "loading" || state.auth.stage !== "credentials") return;
    const normalized = compactInput(username, 120).trim();
    if (state.online === false) {
      setAuth({
        username: normalized,
        submitState: "error",
        error: authenticationError("AUTH_OFFLINE", "Reconnect to sign in to EPIC.")
      });
      return;
    }
    if (!normalized || !password || password.length > 256) {
      setAuth({
        username: normalized,
        submitState: "error",
        error: authenticationError("AUTH_INPUT_REQUIRED", "Enter your EPIC username and password.")
      });
      return;
    }

    const request = controller("auth:login");
    setAuth({ username: normalized, submitState: "loading", error: null });
    try {
      const response = await api.signIn(normalized, password, request.signal);
      if (controllers.get("auth:login") !== request) return;
      await completeSignIn(response);
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      setAuth({ submitState: "error", error: safeError(error) });
    }
  }

  async function signInWithGoogle() {
    if (state.sessionState !== "unauthenticated" || state.auth.stage !== "credentials" || state.auth.submitState === "loading") return;
    const provider = options.googleCredentialProvider;
    if (state.online === false) {
      setAuth({
        submitState: "error",
        error: authenticationError("AUTH_OFFLINE", "Reconnect to continue with Google.")
      });
      return;
    }
    if (provider?.supportsNativeGoogleSignIn !== true) {
      setAuth({
        submitState: "error",
        error: authenticationError("GOOGLE_SIGN_IN_UNAVAILABLE", "Google sign-in is not available in this app build.")
      });
      return;
    }

    const request = controller("auth:google");
    setAuth({ submitState: "loading", error: null });
    try {
      // Finish any earlier config read before issuing this attempt's nonce.
      // Cancelling fetch alone cannot undo a server-side nonce rotation.
      if (authConfigRequest) await authConfigRequest;
      if (controllers.get("auth:google") !== request) return;
      await loadAuthConfig();
      if (controllers.get("auth:google") !== request) return;
      const serverClientId = state.auth.googleClientId;
      const nonce = state.auth.googleNonce;
      if (state.auth.configState !== "ready" || state.auth.googleAvailable !== true || !serverClientId || !nonce) {
        throw authenticationError("GOOGLE_SIGN_IN_UNAVAILABLE", "Google sign-in could not be prepared. Please try again.");
      }
      const nativeResult = await provider.signInWithGoogle({ serverClientId, nonce });
      if (controllers.get("auth:google") !== request) return;
      const credential = compactInput(nativeResult?.idToken, 8192).trim();
      if (!credential) {
        throw Object.assign(new Error("Google did not return a usable credential."), { code: "GOOGLE_CREDENTIAL_INVALID" });
      }
      const response = await api.signInWithGoogle(credential, request.signal);
      if (controllers.get("auth:google") !== request) return;
      await completeSignIn(response);
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (controllers.get("auth:google") !== request) return;
      if (error?.code === "GOOGLE_SIGN_IN_CANCELLED") {
        setAuth({ submitState: "idle", error: null });
      } else {
        setAuth({ submitState: "error", error: safeError(error) });
      }
      void loadAuthConfig();
    }
  }

  async function verifySecondFactor(code: string) {
    const normalized = compactInput(code, 80).trim();
    if (state.online === false) {
      setAuth({
        submitState: "error",
        error: authenticationError("AUTH_OFFLINE", "Reconnect to verify your sign-in.")
      });
      return;
    }
    if (!normalized) {
      setAuth({
        submitState: "error",
        error: authenticationError("TWO_FACTOR_CODE_REQUIRED", "Enter your authenticator or recovery code.")
      });
      return;
    }

    const request = controller("auth:two-factor");
    setAuth({ submitState: "loading", error: null });
    try {
      const response = await api.verifySecondFactor(normalized, request.signal);
      if (controllers.get("auth:two-factor") !== request) return;
      if (response.success !== true) {
        throw Object.assign(new Error("EPIC could not verify this sign-in."), { code: "AUTH_RESPONSE_INVALID" });
      }
      abortAuthenticationRequests();
      state = { ...state, auth: emptyAuthState() };
      emit();
      await loadSession();
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalizedError = safeError(error);
      if (normalizedError.code === "TWO_FACTOR_CHALLENGE_EXPIRED") {
        setAuth({
          stage: "credentials",
          submitState: "error",
          challengeExpiresAt: null,
          error: normalizedError
        });
        void loadAuthConfig();
        return;
      }
      setAuth({ submitState: "error", error: normalizedError });
    }
  }

  function cancelSecondFactor() {
    abortRequest("auth:two-factor");
    setAuth({
      stage: "credentials",
      submitState: "idle",
      challengeExpiresAt: null,
      error: null
    });
    void loadAuthConfig();
  }

  async function resumeSession() {
    // A 401 is expected until the pending sign-in challenge is completed.
    // Refreshing here would reset it or rotate the active Google nonce.
    if (state.sessionState === "loading" || state.online === false) return;
    if (state.sessionState === "unauthenticated"
      && (state.auth.stage === "two-factor" || state.auth.submitState === "loading")) return;
    await loadSession();
    if (state.sessionState === "ready") await refreshLiveInbox();
  }

  async function loadSession() {
    const firstSessionLoad = state.sessionState === "loading" && !state.session && state.guest.loadState === "idle";
    const request = controller("session");
    setState({ sessionState: "loading", toast: null });
    try {
      const session = await api.getSession(request.signal);
      const activeAccount = activeAccountFromSession(session);
      const previousAccount = normalizedUsername(state.activeAccount?.username);
      const nextAccount = normalizedUsername(activeAccount?.username);
      let accountChanged = false;
      if (previousAccount && previousAccount !== nextAccount) {
        accountChanged = true;
        abortAccountScopedRequests();
        state = {
          ...state,
          accountVersion: state.accountVersion + 1,
          profileView: "public",
          publicProfile: emptyPublicProfile(),
          inbox: emptyInboxState(),
          wctek: emptyWctekState(),
          companion: emptyCompanionState(),
          socialLinks: emptySocialLinksState(),
          checkins: emptyCheckinState(),
          reviewDrafts: emptyReviewDraftsState(),
          memoryHydration: emptyMemoryHydrationState(),
          providerConnections: emptyProviderConnectionsState(),
          toast: null
        };
      }
      abortAuthenticationRequests();
      setState({
        sessionState: activeAccount ? "ready" : "forbidden",
        session,
        activeAccount,
        auth: emptyAuthState()
      });
      if (activeAccount) {
        if (accountChanged) void loadUiConfig();
        if (state.activeTab === "profile" && state.profileView === "public") void loadPublicProfile();
        void loadInbox();
        void loadInboxMetadata();
        void loadCompanionSettings();
      }
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      abortAccountScopedRequests();
      clearAccountScopedState();
      setState({
        sessionState: error?.status === 401 ? "unauthenticated" : "error",
        session: null,
        activeAccount: null,
        auth: error?.status === 401 ? emptyAuthState() : state.auth,
        toast: error?.status === 401 ? null : safeError(error).message
      });
      if (error?.status === 401) {
        if (firstSessionLoad && MOBILE_GUEST_ENABLED) setState({ activeTab: "olivia" });
        void loadAuthConfig();
        if (state.activeTab === "olivia") await loadGuest();
      }
    }
  }

  async function loadUiConfig() {
    const version = state.accountVersion;
    const request = controller("mobile:ui");
    setState({ uiState: "loading" });
    try {
      const response = await api.getMobileUi(request.signal);
      if (version !== state.accountVersion) return;
      setState({
        ui: normalizeMobileUiConfig(response.ui),
        uiState: "ready"
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      setState({
        ui: DEFAULT_MOBILE_UI,
        uiState: "error"
      });
    }
  }

  async function logout() {
    abortAuthenticationRequests();
    abortAccountScopedRequests();
    // Fence native background authority at the user's logout action, before
    // waiting for a server response that may be delayed or unavailable.
    state = {
      ...state,
      sessionState: "unauthenticated",
      session: null,
      activeAccount: null,
      auth: emptyAuthState()
    };
    clearAccountScopedState();
    const request = controller("logout");
    try {
      await api.logout(request.signal);
    } catch (_) {
      // A failed logout request still clears local projections. Server state remains authoritative.
    }
    // A delayed logout response must not erase a later explicit sign-in.
    if (state.sessionState === "unauthenticated") void loadAuthConfig();
  }

  function wctekOfflineError(): SafeError {
    return {
      status: 0,
      code: "WCTEK_OFFLINE",
      message: "Reconnect to continue with WCTEK AI. Your draft is still here.",
      retryable: true,
      requiredAction: null,
      correlationRef: null
    };
  }

  function wctekRequestError(error: any) {
    const normalized = safeError(error);
    if (normalized.status === 0 && normalized.code !== "REQUEST_CANCELLED") normalized.retryable = true;
    return normalized;
  }

  async function loadWctekSessions() {
    const account = state.activeAccount;
    if (!account || state.online === false) return;
    const version = state.accountVersion;
    const request = controller("wctek:history");
    setWctek({ historyState: "loading", historyError: null });
    try {
      const response = await api.listWctekSessions(request.signal);
      if (!currentRequest("wctek:history", request, version)) return;
      assertWctekSessionList(response, account);
      const sessions = normalizedWctekSessions(response.sessions);
      setWctek({
        historyState: sessions.length ? "ready" : "empty",
        sessions,
        service: response.service ? normalizedWctekService(response.service) : state.wctek.service,
        historyError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("wctek:history", request, version)) return;
      const normalized = wctekRequestError(error);
      if (handleAccountSecurityError(normalized)) return;
      setWctek({ historyState: "error", historyError: normalized });
    }
  }

  async function loadWctekWorkspace() {
    if (state.sessionState === "unauthenticated") return loadGuest(true);
    const account = state.activeAccount;
    if (!account) return;
    if (state.online === false) {
      setWctek({ loadState: "error", error: wctekOfflineError() });
      return;
    }
    const version = state.accountVersion;
    const request = controller("wctek:workspace");
    setWctek({ loadState: "loading", historyState: "loading", error: null, historyError: null, sendError: null });
    try {
      const history = await api.listWctekSessions(request.signal);
      if (!currentRequest("wctek:workspace", request, version)) return;
      assertWctekSessionList(history, account);
      const sessions = normalizedWctekSessions(history.sessions);
      setWctek({
        historyState: sessions.length ? "ready" : "empty",
        sessions,
        service: history.service ? normalizedWctekService(history.service) : state.wctek.service,
        historyError: null
      });
      const response = sessions.length
        ? await api.getWctekSession(sessions[0].sessionId, request.signal)
        : await api.createWctekSession(request.signal);
      if (!currentRequest("wctek:workspace", request, version)) return;
      const thread = wctekThreadPatch(response, account);
      if (sessions.length && thread.sessionId !== sessions[0].sessionId) {
        throw wctekContextError("WCTEK AI returned a different conversation than the one requested.");
      }
      setWctek({
        loadState: "ready",
        sendState: "idle",
        ...thread,
        composer: "",
        pendingClientMessageId: null,
        service: normalizedWctekService(response.service),
        error: null,
        sendError: null
      });
      if (!sessions.length) void loadWctekSessions();
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("wctek:workspace", request, version)) return;
      const normalized = wctekRequestError(error);
      if (handleAccountSecurityError(normalized)) return;
      setWctek({ loadState: "error", historyState: "error", error: normalized, historyError: normalized });
    }
  }

  async function startNewWctekSession() {
    if (state.wctek.sendState === "loading") cancelWctekSend();
    const account = state.activeAccount;
    if (!account) return;
    if (state.online === false) {
      setWctek({ loadState: "error", error: wctekOfflineError() });
      return;
    }
    const version = state.accountVersion;
    const request = controller("wctek:workspace");
    setWctek({ loadState: "loading", error: null, sendError: null });
    try {
      const response = await api.createWctekSession(request.signal);
      if (!currentRequest("wctek:workspace", request, version)) return;
      const thread = wctekThreadPatch(response, account);
      setWctek({
        loadState: "ready",
        sendState: "idle",
        ...thread,
        composer: "",
        pendingClientMessageId: null,
        service: normalizedWctekService(response.service),
        error: null,
        sendError: null
      });
      void loadWctekSessions();
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("wctek:workspace", request, version)) return;
      const normalized = wctekRequestError(error);
      if (handleAccountSecurityError(normalized)) return;
      setWctek({ loadState: "error", error: normalized });
    }
  }

  async function openWctekSession(sessionId: string) {
    const account = state.activeAccount;
    const normalizedSessionId = compactInput(sessionId, 120).trim();
    if (!account || !WCTEK_SESSION_ID_RE.test(normalizedSessionId) || state.online === false) return;
    if (normalizedSessionId === state.wctek.sessionId) return;
    const version = state.accountVersion;
    const request = controller("wctek:workspace");
    if (state.wctek.sendState === "loading") cancelWctekSend();
    setWctek({ loadState: "loading", error: null, sendError: null });
    try {
      const response = await api.getWctekSession(normalizedSessionId, request.signal);
      if (!currentRequest("wctek:workspace", request, version)) return;
      const thread = wctekThreadPatch(response, account);
      if (thread.sessionId !== normalizedSessionId) {
        throw wctekContextError("WCTEK AI returned a different conversation than the one requested.");
      }
      setWctek({
        loadState: "ready",
        sendState: "idle",
        ...thread,
        composer: "",
        pendingClientMessageId: null,
        service: normalizedWctekService(response.service),
        error: null,
        sendError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("wctek:workspace", request, version)) return;
      const normalized = wctekRequestError(error);
      if (handleAccountSecurityError(normalized)) return;
      setWctek({ loadState: "error", error: normalized });
    }
  }

  function updateWctekComposer(value: string) {
    if (state.sessionState === "unauthenticated") {
      if (!state.guest.busy) setGuest({ composer: compactInput(value, 4000), error: null });
      return;
    }
    if (state.wctek.sendState === "loading") return;
    setWctek({
      composer: compactInput(value, 4000),
      pendingClientMessageId: null,
      sendState: "idle",
      sendError: null
    });
  }

  async function sendWctekMessage() {
    if (state.sessionState === "unauthenticated") return sendGuest();
    const account = state.activeAccount;
    const current = state.wctek;
    const message = current.composer.trim();
    if (!account || !current.threadId || !current.sessionId || !message || current.sendState === "loading") return;
    if (state.online === false) {
      setWctek({ sendState: "error", sendError: wctekOfflineError() });
      return;
    }
    if (current.service?.capabilities?.chat !== true || current.service.status !== "online") {
      setWctek({
        sendState: "forbidden",
        sendError: {
          status: 503,
          code: "CTEK_CHAT_DISABLED",
          message: "WCTEK AI chat is not enabled for this environment.",
          retryable: false,
          requiredAction: null,
          correlationRef: null
        }
      });
      return;
    }
    const clientMessageId = current.pendingClientMessageId || createIdempotencyKey("wctek-chat").slice(0, 120);
    const version = state.accountVersion;
    const threadId = current.threadId;
    const sessionId = current.sessionId;
    const request = controller("wctek:send");
    setWctek({ sendState: "loading", pendingClientMessageId: clientMessageId, sendError: null });
    try {
      const response = await api.sendWctekMessage({ message, clientMessageId, threadId, sessionId }, request.signal);
      if (!currentRequest("wctek:send", request, version)) return;
      const thread = wctekThreadPatch(response, account);
      if (thread.threadId !== threadId || thread.sessionId !== sessionId) {
        throw wctekContextError("WCTEK AI returned a response outside the active conversation.");
      }
      setWctek({
        loadState: "ready",
        sendState: "ready",
        ...thread,
        composer: "",
        pendingClientMessageId: null,
        error: null,
        sendError: null
      });
      void loadWctekSessions();
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("wctek:send", request, version)) return;
      const normalized = wctekRequestError(error);
      if (handleAccountSecurityError(normalized)) return;
      setWctek({
        sendState: normalized.code === "CTEK_CHAT_DISABLED" ? "forbidden" : "error",
        service: normalized.code === "CTEK_CHAT_DISABLED"
          ? { status: "disabled", capabilities: { chat: false } }
          : state.wctek.service,
        sendError: normalized
      });
    }
  }

  function cancelWctekSend() {
    if (state.wctek.sendState !== "loading") return;
    abortRequest("wctek:send");
    setWctek({
      sendState: "error",
      sendError: {
        status: 0,
        code: "WCTEK_SEND_CANCELLED",
        message: "The request was stopped. Your draft is still here and can be retried.",
        retryable: true,
        requiredAction: null,
        correlationRef: null
      }
    });
  }

  async function loadCompanionSettings() {
    if (!state.activeAccount || state.companion.dirty) return;
    const version = state.accountVersion;
    const account = state.activeAccount;
    const request = controller("companion:load");
    setCompanion({ loadState: "loading", error: null, saveError: null });
    try {
      const response = await api.getCompanionSettings(request.signal);
      if (version !== state.accountVersion) return;
      assertCompanionAccount(response, account);
      const saved = companionDraft(response.settings);
      setCompanion({
        loadState: "ready",
        saveState: "idle",
        saved,
        draft: { ...saved },
        policy: response.policyAcceptance || null,
        policyAcceptedByUser: response.policyAcceptance?.accepted === true,
        dirty: false,
        error: null,
        saveError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setCompanion({
        loadState: "error",
        error: normalized
      });
    }
  }

  function updateCompanionSetting(field: MobileUiCompanionFieldKey, value: string) {
    const current = state.companion.draft;
    if (!current || state.companion.loadState !== "ready") return;
    const next = { ...current };
    switch (field) {
      case "ai-name":
        next.aiName = compactInput(value, 80);
        break;
      case "base-personality":
        next.basePersonality = compactInput(value, 1200);
        break;
      case "communication-style":
        next.communicationStyle = allowedValue(value, MOBILE_COMPANION_FIELD_OPTIONS["communication-style"]!, current.communicationStyle);
        break;
      case "hard-refusals":
        next.hardRefusals = compactInput(value, 900);
        break;
      case "soft-boundaries":
        next.softBoundaries = compactInput(value, 900);
        break;
      case "relationship-boundaries":
        next.relationshipBoundaries = allowedValue(value, MOBILE_COMPANION_FIELD_OPTIONS["relationship-boundaries"]!, current.relationshipBoundaries);
        break;
      case "ai-reminder-frequency":
        next.aiReminderFrequency = allowedValue(value, MOBILE_COMPANION_FIELD_OPTIONS["ai-reminder-frequency"]!, current.aiReminderFrequency);
        break;
      case "redirect-to-real-life":
        next.redirectToRealLife = allowedValue(value, MOBILE_COMPANION_FIELD_OPTIONS["redirect-to-real-life"]!, current.redirectToRealLife);
        break;
    }
    setCompanion({
      draft: next,
      dirty: !sameDraft(state.companion.saved, next),
      saveState: "idle",
      saveError: null
    });
  }

  function setCompanionPolicyAccepted(accepted: boolean) {
    if (state.companion.policy?.accepted === true || state.companion.policy?.documentsAvailable !== true) return;
    setCompanion({ policyAcceptedByUser: accepted, saveState: "idle", saveError: null });
  }

  async function saveCompanionSettings() {
    const account = state.activeAccount;
    const current = state.companion;
    if (!account || !current.saved || !current.draft || !current.dirty || state.online === false) return;
    const policiesAccepted = current.policy?.accepted === true;
    const canAcceptPolicies = current.policy?.documentsAvailable === true && current.policyAcceptedByUser;
    if (!policiesAccepted && !canAcceptPolicies) return;
    const patch = companionPatch(current.saved, current.draft);
    if (!Object.keys(patch).length) {
      setCompanion({ dirty: false, saveState: "ready", saveError: null });
      return;
    }
    const version = state.accountVersion;
    const request = controller("companion:save");
    setCompanion({ saveState: "loading", saveError: null });
    try {
      const response = await api.saveCompanionSettings(patch, {
        acceptPolicies: !policiesAccepted && canAcceptPolicies,
        signal: request.signal
      });
      if (version !== state.accountVersion) return;
      assertCompanionAccount(response, account);
      const saved = companionDraft(response.settings);
      setCompanion({
        loadState: "ready",
        saveState: "ready",
        saved,
        draft: { ...saved },
        policy: response.policyAcceptance || current.policy,
        policyAcceptedByUser: response.policyAcceptance?.accepted === true || current.policyAcceptedByUser,
        dirty: false,
        error: null,
        saveError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setCompanion({ saveState: "error", saveError: normalized });
    }
  }

  async function loadPublicProfile() {
    const account = state.activeAccount;
    if (!account || state.sessionState !== "ready" || state.publicProfile.loadState === "loading") return;
    if (!state.online) {
      setState({ publicProfile: { ...state.publicProfile, loadState: "error", error: authenticationError("PROFILE_OFFLINE", "Reconnect to refresh your profile.") } });
      return;
    }
    const version = state.accountVersion;
    const request = controller("profile:public");
    setState({ publicProfile: { ...state.publicProfile, loadState: "loading", error: null } });
    try {
      const response = await api.getPublicProfile(account.username, request.signal);
      if (!currentRequest("profile:public", request, version)) return;
      const data = normalizePublicProfile(response.profile, account.username);
      setState({ publicProfile: { loadState: "ready", data, error: null } });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED" || !currentRequest("profile:public", request, version)) return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setState({ publicProfile: {
        loadState: "error", data: normalized.code === "PUBLIC_PROFILE_ACCOUNT_MISMATCH" ? null : state.publicProfile.data,
        error: { ...normalized, message: normalized.status === 404 ? "Your profile is not available in this app yet. You can still open it on the website." : normalized.message }
      } });
    }
  }

  async function loadSocialLinks(options: { force?: boolean } = {}) {
    const account = state.activeAccount;
    if (!account || (state.socialLinks.dirty && !options.force)) return;
    const version = state.accountVersion;
    const request = controller("social-links:load");
    setSocialLinks({ loadState: "loading", error: null, saveError: null });
    try {
      const response = await api.getSocialLinks(account.username, request.signal);
      if (version !== state.accountVersion) return;
      const saved = normalizedSocialLinks(response.socialLinks);
      setSocialLinks({
        loadState: saved.length ? "ready" : "empty",
        saveState: "idle",
        enabled: response.enabled !== false,
        savedEnabled: response.enabled !== false,
        saved,
        draft: saved.map((item) => ({ ...item })),
        revision: Number.isSafeInteger(response.revision) ? response.revision : 0,
        csrfToken: compactInput(response.csrfToken, 256).trim(),
        dirty: false,
        error: null,
        saveError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setSocialLinks({ loadState: normalized.status === 403 ? "forbidden" : "error", error: normalized });
    }
  }

  function setSocialLinksEnabled(enabled: boolean) {
    if (!["ready", "empty"].includes(state.socialLinks.loadState)) return;
    setSocialLinks({ enabled, dirty: !sameValue(state.socialLinks.saved, state.socialLinks.draft) || enabled !== state.socialLinks.savedEnabled, saveState: "idle", saveError: null });
  }

  function addSocialLink() {
    if (!["ready", "empty"].includes(state.socialLinks.loadState) || state.socialLinks.draft.length >= 25) return;
    const draft = [...state.socialLinks.draft, {
      id: newSocialLinkId(),
      platform: "instagram",
      label: "Instagram",
      url: "",
      visible: true,
      imageAssetId: null,
      customPlatform: false,
      platformName: null,
      size: "medium"
    }];
    setSocialLinks({ draft, dirty: true, saveState: "idle", saveError: null, loadState: "ready" });
  }

  function updateSocialLink(index: number, field: "platform" | "label" | "url" | "visible", value: string | boolean) {
    const current = state.socialLinks.draft[index];
    if (!current || !["ready", "empty"].includes(state.socialLinks.loadState)) return;
    const draft = state.socialLinks.draft.map((item) => ({ ...item }));
    const next = draft[index];
    if (field === "platform") {
      const platform = MOBILE_SOCIAL_PLATFORM_OPTIONS.includes(value as typeof MOBILE_SOCIAL_PLATFORM_OPTIONS[number]) ? String(value) : current.platform;
      next.platform = platform;
      next.customPlatform = false;
      next.platformName = null;
      if (platform !== current.platform) next.imageAssetId = null;
    } else if (field === "label") next.label = compactInput(value, 80);
    else if (field === "url") next.url = compactInput(value, 2048);
    else next.visible = value === true;
    setSocialLinks({ draft, dirty: !sameValue(state.socialLinks.saved, draft) || state.socialLinks.enabled !== state.socialLinks.savedEnabled, saveState: "idle", saveError: null });
  }

  function moveSocialLink(index: number, direction: -1 | 1) {
    const destination = index + direction;
    if (!state.socialLinks.draft[index] || destination < 0 || destination >= state.socialLinks.draft.length) return;
    const draft = state.socialLinks.draft.map((item) => ({ ...item }));
    [draft[index], draft[destination]] = [draft[destination], draft[index]];
    setSocialLinks({ draft, dirty: true, saveState: "idle", saveError: null });
  }

  function removeSocialLink(index: number) {
    if (!state.socialLinks.draft[index]) return;
    const draft = state.socialLinks.draft.filter((_, itemIndex) => itemIndex !== index);
    setSocialLinks({ draft, dirty: !sameValue(state.socialLinks.saved, draft) || state.socialLinks.enabled !== state.socialLinks.savedEnabled, saveState: "idle", saveError: null, loadState: draft.length ? "ready" : "empty" });
  }

  async function saveSocialLinks() {
    const account = state.activeAccount;
    const current = state.socialLinks;
    if (!account || !current.dirty || state.online === false || !current.csrfToken) return;
    if (current.draft.some((item) => !item.label.trim() || !item.url.trim())) {
      setSocialLinks({
        saveState: "error",
        saveError: { status: 400, code: "SOCIAL_LINKS_INVALID", message: "Each social link needs a label and profile URL.", retryable: false, requiredAction: null, correlationRef: null }
      });
      return;
    }
    const version = state.accountVersion;
    const request = controller("social-links:save");
    setSocialLinks({ saveState: "loading", saveError: null });
    try {
      const response = await api.saveSocialLinks(account.username, {
        enabled: current.enabled,
        expectedRevision: current.revision,
        socialLinks: current.draft.map((item) => ({
          id: item.id,
          platform: item.platform,
          label: item.label.trim(),
          url: item.url.trim(),
          visible: item.visible,
          imageAssetId: item.imageAssetId || null,
          customPlatform: item.customPlatform === true,
          platformName: item.platformName || null,
          size: item.size || "medium"
        }))
      }, { csrfToken: current.csrfToken, signal: request.signal });
      if (version !== state.accountVersion) return;
      const saved = normalizedSocialLinks(response.socialLinks);
      setSocialLinks({
        loadState: saved.length ? "ready" : "empty",
        saveState: "ready",
        enabled: response.enabled !== false,
        savedEnabled: response.enabled !== false,
        saved,
        draft: saved.map((item) => ({ ...item })),
        revision: Number.isSafeInteger(response.revision) ? response.revision : current.revision,
        csrfToken: compactInput(response.csrfToken, 256).trim() || current.csrfToken,
        dirty: false,
        error: null,
        saveError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setSocialLinks({ saveState: "error", saveError: normalized });
    }
  }

  async function loadCheckinSettings(options: { force?: boolean } = {}) {
    if (!state.activeAccount || (state.checkins.dirty && !options.force)) return;
    const version = state.accountVersion;
    const request = controller("checkins:load");
    setCheckins({ loadState: "loading", error: null, saveError: null });
    try {
      const response = await api.getCheckinPreferences(request.signal);
      if (version !== state.accountVersion) return;
      const saved = checkinDraft(response.preferences);
      setCheckins({ loadState: "ready", saveState: "idle", saved, draft: { ...saved, slots: saved.slots.map((slot) => ({ ...slot })), quietHours: { ...saved.quietHours } }, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setCheckins({ loadState: normalized.status === 403 ? "forbidden" : "error", error: normalized });
    }
  }

  function updateCheckinSetting(field: string, value: string | boolean) {
    const current = state.checkins.draft;
    if (!current || state.checkins.loadState !== "ready") return;
    const draft: CheckinSettingsDraft = { ...current, slots: current.slots.map((slot) => ({ ...slot })), quietHours: { ...current.quietHours } };
    if (field === "frequency") {
      const frequency = Math.max(0, Math.min(3, Number(value) || 0));
      draft.slots = draft.slots.map((slot, index) => ({ ...slot, enabled: index < frequency }));
      draft.frequency = frequency;
    } else if (field === "timezone") draft.timezone = compactInput(value, 80);
    else if (field === "quiet-enabled") draft.quietHours.enabled = value === true;
    else if (field === "quiet-start") draft.quietHours.start = String(value);
    else if (field === "quiet-end") draft.quietHours.end = String(value);
    else if (field === "paused-until") draft.pausedUntil = String(value);
    else {
      const match = /^slot-(morning|afternoon|evening)-(time|enabled)$/.exec(field);
      if (!match) return;
      const slot = draft.slots.find((item) => item.id === match[1]);
      if (!slot) return;
      if (match[2] === "time") slot.time = String(value);
      else slot.enabled = value === true;
      draft.frequency = draft.slots.filter((item) => item.enabled).length;
    }
    setCheckins({ draft, dirty: !sameValue(state.checkins.saved, draft), saveState: "idle", saveError: null });
  }

  function useDeviceTimezone() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) updateCheckinSetting("timezone", timezone);
  }

  async function saveCheckinSettings() {
    const current = state.checkins;
    if (!state.activeAccount || !current.saved || !current.draft || !current.dirty || state.online === false) return;
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: current.draft.timezone }).format(new Date());
    } catch (_) {
      setCheckins({ saveState: "error", saveError: { status: 400, code: "INVALID_TIMEZONE", message: "Enter a valid IANA timezone, such as America/New_York.", retryable: false, requiredAction: null, correlationRef: null } });
      return;
    }
    const patch = checkinPatch(current.saved, current.draft);
    if (!Object.keys(patch).length) {
      setCheckins({ dirty: false, saveState: "ready", saveError: null });
      return;
    }
    const version = state.accountVersion;
    const request = controller("checkins:save");
    setCheckins({ saveState: "loading", saveError: null });
    try {
      const response = await api.saveCheckinPreferences(patch, request.signal);
      if (version !== state.accountVersion) return;
      const saved = checkinDraft(response.preferences);
      setCheckins({ loadState: "ready", saveState: "ready", saved, draft: { ...saved, slots: saved.slots.map((slot) => ({ ...slot })), quietHours: { ...saved.quietHours } }, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setCheckins({ saveState: "error", saveError: normalized });
    }
  }

  async function resetCheckinSettings() {
    if (!state.activeAccount || state.online === false) return;
    const version = state.accountVersion;
    const request = controller("checkins:save");
    setCheckins({ saveState: "loading", saveError: null });
    try {
      const response = await api.resetCheckinPreferences(request.signal);
      if (version !== state.accountVersion) return;
      const saved = checkinDraft(response.preferences);
      setCheckins({ loadState: "ready", saveState: "ready", saved, draft: { ...saved, slots: saved.slots.map((slot) => ({ ...slot })), quietHours: { ...saved.quietHours } }, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setCheckins({ saveState: "error", saveError: normalized });
    }
  }

  async function loadReviewDrafts() {
    const account = state.activeAccount;
    if (!account || state.reviewDrafts.dirty) return;
    const version = state.accountVersion;
    const request = controller("review-drafts:load");
    setReviewDrafts({ loadState: "loading", error: null, saveError: null });
    try {
      const response = await api.listReviewDrafts(request.signal);
      if (version !== state.accountVersion) return;
      assertAccountUsername(response.accountUsername, account, "Drafts were returned for a different EPIC account.", "REVIEW_DRAFT_ACCOUNT_MISMATCH");
      const drafts = (Array.isArray(response.drafts) ? response.drafts : []).slice(0, 100).map((draft) => ({
        ...draft,
        draftId: compactInput(draft.draftId, 80).trim(),
        safeDisplayName: compactInput(draft.safeDisplayName, 160).trim(),
        draftText: compactInput(draft.draftText, 4000),
        inboundExcerpt: compactInput(draft.inboundExcerpt, 500).trim(),
        provider: compactInput(draft.provider, 80).trim()
      })).filter((draft) => draft.draftId);
      const selected = drafts.find((draft) => draft.draftId === state.reviewDrafts.selectedDraftId) || drafts.find((draft) => !["rejected", "human_takeover"].includes(String(draft.status))) || drafts[0] || null;
      setReviewDrafts({
        loadState: drafts.length ? "ready" : "empty",
        saveState: "idle",
        drafts,
        selectedDraftId: selected?.draftId || null,
        savedText: selected?.draftText || "",
        draftText: selected?.draftText || "",
        dirty: false,
        error: null,
        saveError: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setReviewDrafts({ loadState: normalized.status === 403 ? "forbidden" : "error", error: normalized });
    }
  }

  function selectReviewDraft(draftId: string) {
    if (state.reviewDrafts.dirty) return;
    const selected = state.reviewDrafts.drafts.find((draft) => draft.draftId === draftId);
    if (!selected) return;
    setReviewDrafts({ selectedDraftId: selected.draftId, savedText: selected.draftText, draftText: selected.draftText, dirty: false, saveState: "idle", saveError: null });
  }

  function updateReviewDraftText(value: string) {
    if (!state.reviewDrafts.selectedDraftId) return;
    const draftText = compactInput(value, 4000);
    setReviewDrafts({ draftText, dirty: draftText !== state.reviewDrafts.savedText, saveState: "idle", saveError: null });
  }

  async function saveReviewDraft() {
    const current = state.reviewDrafts;
    if (!state.activeAccount || !current.selectedDraftId || !current.dirty || state.online === false) return;
    const version = state.accountVersion;
    const request = controller("review-drafts:save");
    setReviewDrafts({ saveState: "loading", saveError: null });
    try {
      const response = await api.saveReviewDraft(current.selectedDraftId, current.draftText, request.signal);
      if (version !== state.accountVersion) return;
      const draft = response.draft;
      const drafts = state.reviewDrafts.drafts.map((item) => item.draftId === draft.draftId ? draft : item);
      setReviewDrafts({ loadState: "ready", saveState: "ready", drafts, savedText: draft.draftText, draftText: draft.draftText, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setReviewDrafts({ saveState: "error", saveError: normalized });
    }
  }

  async function markReviewDraft(action: "reject" | "human_takeover") {
    const current = state.reviewDrafts;
    if (!state.activeAccount || !current.selectedDraftId || current.dirty || state.online === false) return;
    const version = state.accountVersion;
    const request = controller("review-drafts:save");
    setReviewDrafts({ saveState: "loading", saveError: null });
    try {
      const response = action === "reject"
        ? await api.rejectReviewDraft(current.selectedDraftId, request.signal)
        : await api.takeOverReviewDraft(current.selectedDraftId, request.signal);
      if (version !== state.accountVersion) return;
      const drafts = state.reviewDrafts.drafts.map((item) => item.draftId === response.draft.draftId ? response.draft : item);
      setReviewDrafts({ loadState: "ready", saveState: "ready", drafts, savedText: response.draft.draftText, draftText: response.draft.draftText, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setReviewDrafts({ saveState: "error", saveError: normalized });
    }
  }

  async function loadMemoryHydration() {
    const account = state.activeAccount;
    if (!account || state.memoryHydration.dirty) return;
    const version = state.accountVersion;
    const request = controller("memory:load");
    setMemoryHydration({ loadState: "loading", error: null, saveError: null });
    try {
      const response = await api.getWynterProfile(request.signal);
      if (version !== state.accountVersion) return;
      assertAccountUsername(response.account?.username, account, "Memory settings were returned for a different EPIC account.", "MEMORY_ACCOUNT_MISMATCH");
      const consent = consentFromProfile(response);
      setMemoryHydration({ loadState: "ready", saveState: "idle", data: response, savedConsent: consent, draftConsent: { ...consent }, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setMemoryHydration({ loadState: normalized.status === 403 ? "forbidden" : "error", error: normalized });
    }
  }

  function updateMemoryConsent(field: "styleLearningEnabled" | "conversationalUploadEnabled", value: boolean) {
    const current = state.memoryHydration.draftConsent;
    if (!current || state.memoryHydration.loadState !== "ready") return;
    const draftConsent = { ...current, [field]: value };
    setMemoryHydration({ draftConsent, dirty: !sameValue(state.memoryHydration.savedConsent, draftConsent), saveState: "idle", saveError: null });
  }

  async function saveMemoryConsent() {
    const current = state.memoryHydration;
    const account = state.activeAccount;
    if (!account || !current.draftConsent || !current.dirty || state.online === false) return;
    const version = state.accountVersion;
    const request = controller("memory:save");
    setMemoryHydration({ saveState: "loading", saveError: null });
    try {
      const response = await api.saveWynterProfileConsent(current.draftConsent, request.signal);
      if (version !== state.accountVersion) return;
      assertAccountUsername(response.account?.username, account, "Memory settings were returned for a different EPIC account.", "MEMORY_ACCOUNT_MISMATCH");
      const consent = consentFromProfile(response);
      setMemoryHydration({ loadState: "ready", saveState: "ready", data: response, savedConsent: consent, draftConsent: { ...consent }, dirty: false, error: null, saveError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setMemoryHydration({ saveState: "error", saveError: normalized });
    }
  }

  function selectMemoryUploadFile(file: File | null) {
    const allowed = file && /\.(txt|html|json|jsonl|csv|zip)$/i.test(file.name);
    const tooLarge = file && file.size > 40 * 1024 * 1024;
    if (file && (!allowed || tooLarge)) {
      setMemoryHydration({
        selectedFile: null,
        uploadConsent: false,
        uploadState: "error",
        uploadError: { status: 400, code: tooLarge ? "MOBILE_UPLOAD_TOO_LARGE" : "MOBILE_UPLOAD_TYPE_UNSUPPORTED", message: tooLarge ? "Mobile imports are limited to 40 MB. Use the desktop AI Manager for larger imports." : "Choose a .txt, .html, .json, .jsonl, .csv, or .zip file.", retryable: false, requiredAction: null, correlationRef: null }
      });
      return;
    }
    setMemoryHydration({ selectedFile: file, uploadConsent: false, uploadState: "idle", uploadStatus: null, uploadError: null });
  }

  function setMemoryUploadConsent(value: boolean) {
    if (!state.memoryHydration.selectedFile) return;
    setMemoryHydration({ uploadConsent: value, uploadState: "idle", uploadError: null });
  }

  async function uploadMemoryFile() {
    const current = state.memoryHydration;
    if (!state.activeAccount || !current.selectedFile || !current.uploadConsent || state.online === false) return;
    const account = state.activeAccount;
    const version = state.accountVersion;
    const request = controller("memory:upload");
    setMemoryHydration({ uploadState: "loading", uploadError: null, uploadStatus: null });
    try {
      await api.uploadConversationalData(current.selectedFile, request.signal);
      if (version !== state.accountVersion || normalizedUsername(state.activeAccount?.username) !== normalizedUsername(account.username)) return;
      setMemoryHydration({ uploadState: "ready", selectedFile: null, uploadConsent: false, uploadStatus: "Conversation data imported.", uploadError: null });
      await loadMemoryHydration();
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setMemoryHydration({ uploadState: "error", uploadError: normalized });
    }
  }

  async function loadProviderConnections(options: { preserveStatus?: boolean } = {}) {
    if (!state.activeAccount) return;
    const version = state.accountVersion;
    const request = controller("providers:load");
    setProviderConnections({
      loadState: "loading",
      error: null,
      actionError: null,
      status: options.preserveStatus ? state.providerConnections.status : null
    });
    try {
      const response = await api.listProviderConnections(request.signal);
      if (version !== state.accountVersion) return;
      const connections = (Array.isArray(response.connections) ? response.connections : [])
        .map(normalizeProviderConnection)
        .filter((view): view is ProviderConnectionView => Boolean(view));
      setProviderConnections({ loadState: connections.length ? "ready" : "empty", connections, error: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setProviderConnections({ loadState: normalized.status === 403 ? "forbidden" : "error", error: normalized });
    }
  }

  async function executeProviderAction(targetRef: string, action: ProviderConnectionActionName) {
    const connection = state.providerConnections.connections.find((item) => item.targetRef === targetRef);
    const configured = connection?.actions?.find((item) => item.name === action && item.enabled === true);
    if (!state.activeAccount || !connection || !configured || !PROVIDER_ACTIONS.includes(action) || state.online === false || state.providerConnections.actionState === "loading") return null;
    const version = state.accountVersion;
    const request = controller("providers:action");
    const activeActionKey = `${targetRef}:${action}`;
    setProviderConnections({ actionState: "loading", activeActionKey, actionError: null, status: null });
    try {
      let response = await api.executeProviderConnectionAction(targetRef, action, request.signal);
      if (version !== state.accountVersion || !response) return null;
      if (response.setup?.required) {
        if (connection.platform !== "textbee" || response.setup.type !== "inline_setup" || response.setup.schema !== "textbee_gateway.v1") {
          throw Object.assign(new Error("This connection setup is unavailable."), { code: "PLATFORM_CONNECTION_FLOW_UNAVAILABLE", status: 409 });
        }
        const requestSetup = options.requestProviderSetup || (await import("../../public/shared/manager/textBeeGatewaySetup.js")).requestTextBeeSetup;
        const setup = await requestSetup({ signal: request.signal });
        if (version !== state.accountVersion || request.signal.aborted) return null;
        if (!setup) {
          setProviderConnections({ actionState: "idle", activeActionKey: null });
          return null;
        }
        response = await api.executeProviderConnectionAction(targetRef, action, request.signal, setup);
        if (version !== state.accountVersion || request.signal.aborted) return null;
      }
      const handoffPath = boundedProviderText(response.connectPath || response.handoff?.path, 500);
      setProviderConnections({ actionState: "ready", activeActionKey: null, actionError: null, status: handoffPath ? "Continue in the secure provider sign-in window." : action === "disconnect" ? "Disconnected from EPIC. Turn off Receive SMS in TextBee to stop phone uploads." : "Connection status refreshed." });
      if (!handoffPath) void loadProviderConnections({ preserveStatus: true });
      return handoffPath || null;
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return null;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return null;
      setProviderConnections({ actionState: "error", activeActionKey: null, actionError: normalized, status: null });
      return null;
    }
  }

  function inboxItemKey(item: InboxListItem) {
    if (item.recordKind === "history_review") {
      return item.historyReviewRef ? `history:${item.historyReviewRef}` : null;
    }
    return item.conversationRef ? `conversation:${item.conversationRef}` : null;
  }

  function dedupeInboxItems(items: InboxListItem[]) {
    const byRef = new Map<string, InboxListItem>();
    for (const item of items) {
      const key = inboxItemKey(item);
      if (key && !byRef.has(key)) byRef.set(key, item);
    }
    return [...byRef.values()];
  }

  function conversationFromItem(item: InboxListItem): InboxConversation | null {
    return item.recordKind === "history_review" ? null : item;
  }

  async function loadInboxMetadata() {
    if (!state.activeAccount) return;
    const version = state.accountVersion;
    const request = controller("inbox:metadata");
    setInbox({ metadataState: "loading", metadataError: null });
    try {
      const [folderResponse, labelResponse] = await Promise.all([
        api.listInboxFolders(request.signal),
        api.listInboxLabels(request.signal)
      ]);
      if (!currentRequest("inbox:metadata", request, version)) return;
      const folders = (folderResponse.folders || []).filter((folder) => (
        INBOX_FOLDERS.has(folder.folderId)
        && typeof folder.name === "string"
      )).map((folder) => ({
        folderId: folder.folderId,
        name: compactInput(folder.name, 80).trim(),
        count: Math.max(0, Number(folder.count) || 0)
      }));
      const labels = (labelResponse.labels || []).filter((label) => (
        /^[A-Za-z0-9._:-]{1,80}$/.test(String(label.labelId || ""))
        && typeof label.name === "string"
      )).slice(0, 100).map((label) => ({
        labelId: String(label.labelId),
        name: compactInput(label.name, 80).trim()
      }));
      const allowedLabels = new Set(labels.map((label) => label.labelId));
      const filterDraft = {
        ...state.inbox.filterDraft,
        labelIds: state.inbox.filterDraft.labelIds.filter((labelId) => allowedLabels.has(labelId))
      };
      setInbox({ folders, labels, filterDraft, metadataState: "ready", metadataError: null });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      if (!currentRequest("inbox:metadata", request, version)) return;
      setInbox({ metadataState: normalized.status === 403 ? "forbidden" : "error", metadataError: normalized });
    }
  }

  async function loadInbox(cursor: string | null = null, options: { background?: boolean } = {}) {
    if (!state.activeAccount) return;
    const version = state.accountVersion;
    const filters = cloneInboxFilters(state.inbox.filters);
    const requestKey = options.background ? "inbox:list:live" : "inbox:list";
    const request = controller(requestKey);
    if (!options.background) {
      setInbox({
        listState: cursor ? state.inbox.listState : "loading",
        error: null
      });
    }
    try {
      const page = await api.listInboxConversations({
        ...filters,
        view: "contacts",
        cursor,
        limit: 20,
        signal: request.signal
      });
      if (!currentRequest(requestKey, request, version) || !sameValue(filters, state.inbox.filters)) return;
      const conversations = cursor
        ? dedupeInboxItems([...state.inbox.conversations, ...page.conversations])
        : options.background
          ? dedupeInboxItems([...page.conversations, ...state.inbox.conversations])
          : dedupeInboxItems(page.conversations);
      const preservePagination = options.background && state.inbox.page !== null;
      setInbox({
        conversations,
        page: preservePagination ? state.inbox.page : page.page,
        loadedConversationPages: cursor
          ? state.inbox.loadedConversationPages + 1
          : options.background
            ? Math.max(1, state.inbox.loadedConversationPages)
            : 1,
        listState: conversations.length ? "ready" : "empty",
        error: null
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest(requestKey, request, version) || !sameValue(filters, state.inbox.filters)) return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      if (!options.background || state.inbox.listState !== "ready") {
        setInbox({
          listState: normalized.status === 403 ? "forbidden" : "error",
          error: normalized
        });
      }
    }
  }

  async function loadConversation(conversationRef: string, options: { background?: boolean } = {}) {
    const reference = String(conversationRef || "").trim();
    if (!reference || !state.activeAccount) return;
    const version = state.accountVersion;
    const request = controller("inbox:detail");
    const changingConversation = state.inbox.selectedConversation?.conversationRef !== reference;
    const reconciliationThrough = state.inbox.messages[0] || null;
    if (!options.background) {
      rememberInboxDraft();
      abortRequest("inbox:history");
      abortRequest("inbox:live");
      if (changingConversation) {
        abortRequest("inbox:compose");
        abortRequest("inbox:draft-review");
      }
      setInbox({
        detailState: "loading",
        historyState: "idle",
        historyError: null,
        contextState: "loading",
        detailError: null,
        contextError: null,
        selectedConversation: state.inbox.conversations
          .map(conversationFromItem)
          .find((item) => item?.conversationRef === reference) || null,
        messages: [],
        messagePage: null,
        historyCoverage: null,
        liveReconciliation: null,
        liveState: "idle",
        liveError: null,
        loadedMessagePages: 0,
        historySynchronization: null,
        contactContext: changingConversation ? null : state.inbox.contactContext,
        ...(changingConversation ? {
          composerMode: "reply" as InboxComposerMode,
          composerText: "",
          composerState: "idle" as LoadState,
          composerError: null,
          composerIdempotencyKey: null,
          composerStatus: null,
          ...emptyInboxDraftReviewState(),
          ...inboxDrafts.get(`${version}:${reference}`)
        } : {})
      });
    }
    try {
      const [conversation, messages, contactResult] = await Promise.all([
        api.getConversation(reference, request.signal),
        api.listMessages(reference, { limit: 30, signal: request.signal }),
        api.getConversationContact(reference, request.signal)
          .then((context) => ({ context, error: null }))
          .catch((error) => ({ context: null, error: safeError(error) }))
      ]);
      if (!currentRequest("inbox:detail", request, version)) return;
      if (options.background && state.inbox.selectedConversation?.conversationRef !== reference) return;
      if (conversation.conversation?.conversationRef !== reference || messages.conversationRef !== reference) {
        throw inboxComposerFailure("INBOX_CONTEXT_SCOPE_MISMATCH", "This conversation could not be verified.", true);
      }
      const preservePagination = options.background && state.inbox.messagePage !== null && reconciliationThrough !== null;
      const defaultComposerMode = changingConversation
        && conversation.conversation.capabilities?.reply?.enabled !== true
        && conversation.conversation.capabilities?.internalNote?.enabled === true
          ? "note" as InboxComposerMode
          : state.inbox.composerMode;
      const incomingDraft = conversation.conversation.reviewDraft || null;
      const incomingRevision = incomingDraft?.revision || null;
      const returnedContext = contactResult.context?.conversationRef === reference ? contactResult.context : null;
      const contextError = contactResult.error || (contactResult.context ? {
        status: 502,
        code: "INBOX_CONTEXT_SCOPE_MISMATCH",
        message: "Contact context could not be verified.",
        retryable: true,
        requiredAction: null,
        correlationRef: null
      } as SafeError : null);
      const replaceDraftReview = !state.inbox.draftReviewDirty
        && (changingConversation || incomingRevision !== state.inbox.draftReviewRevision);
      const reconciliationNeeded = options.background && messages.page.hasMore && messages.page.nextCursor
        && reconciliationThrough && (!messages.messages.length || compareMessages(messages.messages[0], reconciliationThrough) > 0);
      setInbox({
        selectedConversation: conversation.conversation,
        messages: options.background
          ? mergeMessages(state.inbox.messages, messages.messages)
          : mergeMessages([], messages.messages),
        messagePage: preservePagination ? state.inbox.messagePage : messages.page,
        historyCoverage: messages.historyCoverage || state.inbox.historyCoverage,
        liveReconciliation: options.background
          ? state.inbox.liveReconciliation || (reconciliationNeeded ? { cursor: messages.page.nextCursor!, through: reconciliationThrough } : null)
          : null,
        loadedMessagePages: options.background ? Math.max(1, state.inbox.loadedMessagePages) : 1,
        historySynchronization: messages.historySynchronization || null,
        detailState: "ready",
        detailError: null,
        contactContext: returnedContext || (options.background ? state.inbox.contactContext : null),
        contextState: returnedContext
          ? "ready"
          : options.background && state.inbox.contactContext
            ? "ready"
            : contextError?.status === 403 ? "forbidden" : "error",
        contextError: returnedContext ? null : contextError,
        composerMode: defaultComposerMode,
        ...(replaceDraftReview ? {
          draftReviewText: incomingDraft?.text || "",
          draftReviewDirty: false,
          draftReviewRevision: incomingRevision,
          draftReviewError: null,
          draftReviewIdempotencyKey: null,
          draftReviewAction: null,
          draftSendConfirmed: false
        } : {})
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("inbox:detail", request, version)) return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      if (!options.background || state.inbox.detailState !== "ready") {
        setInbox({
          detailState: normalized.status === 403 ? "forbidden" : "error",
          contextState: normalized.status === 403 ? "forbidden" : "error",
          detailError: normalized,
          contextError: normalized
        });
      } else {
        setInbox({ liveState: "error", liveError: normalized });
      }
    }
  }

  async function openConversation(conversationRef: string) {
    await loadConversation(conversationRef);
  }

  function closeConversation() {
    rememberInboxDraft();
    abortRequest("inbox:detail");
    abortRequest("inbox:history");
    abortRequest("inbox:live");
    abortRequest("inbox:compose");
    abortRequest("inbox:draft-review");
    setInbox({
      detailState: "idle",
      historyState: "idle",
      historyError: null,
      contextState: "idle",
      selectedConversation: null,
      contactContext: null,
      messages: [],
      messagePage: null,
      historyCoverage: null,
      liveReconciliation: null,
      liveState: "idle",
      liveError: null,
      loadedMessagePages: 0,
      detailError: null,
      contextError: null,
      historySynchronization: null,
      composerMode: "reply",
      composerText: "",
      composerState: "idle",
      composerError: null,
      composerIdempotencyKey: null,
      composerStatus: null,
      ...emptyInboxDraftReviewState()
    });
  }

  async function reconcileInboxMessages(reference: string, version: number) {
    if (!state.inbox.liveReconciliation || state.inbox.selectedConversation?.conversationRef !== reference) return;
    const request = controller("inbox:live");
    setInbox({ liveState: "loading", liveError: null });
    try {
      // Revisit the loaded timeline to reconcile delayed history, edits and
      // deletes. The continuation is independent of the upward paging cursor.
      // Each poll is bounded; interrupted catch-up resumes on the next poll.
      for (let count = 0; count < 4 && state.inbox.liveReconciliation; count++) {
        const progress = state.inbox.liveReconciliation;
        const page = await api.listMessages(reference, { cursor: progress.cursor, limit: 30, signal: request.signal });
        if (!currentRequest("inbox:live", request, version) || state.inbox.selectedConversation?.conversationRef !== reference) return;
        if (page.conversationRef !== reference) throw inboxComposerFailure("INBOX_CONTEXT_SCOPE_MISMATCH", "This conversation could not be verified.", true);
        if (page.page.hasMore && (!page.page.nextCursor || page.page.nextCursor === progress.cursor)) {
          throw inboxComposerFailure("INBOX_HISTORY_CURSOR_STALLED", "History refresh could not advance. Retry to continue.", true);
        }
        const reached = !page.page.hasMore || !progress.through
          || page.messages.some(message => compareMessages(message, progress.through!) <= 0);
        const inLoadedWindow = progress.through && state.inbox.messagePage?.hasMore !== false
          ? page.messages.filter(message => compareMessages(message, progress.through!) >= 0) : page.messages;
        setInbox({
          messages: mergeMessages(state.inbox.messages, inLoadedWindow),
          liveReconciliation: reached ? null : { cursor: page.page.nextCursor!, through: progress.through },
          ...(reached && state.inbox.messagePage?.hasMore === false ? { messagePage: page.page } : {}),
          historyCoverage: page.historyCoverage || state.inbox.historyCoverage,
          historySynchronization: page.historySynchronization || state.inbox.historySynchronization
        });
      }
      setInbox({ liveState: "ready", liveError: null });
    } catch (error: any) {
      if (!currentRequest("inbox:live", request, version) || state.inbox.selectedConversation?.conversationRef !== reference) return;
      if (error?.code === "REQUEST_CANCELLED") return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setInbox({ liveState: "error", liveError: normalized });
    }
  }

  async function refreshLiveInbox() {
    if (!state.activeAccount || state.sessionState !== "ready" || state.online === false) return;
    if (liveInboxRequest) return liveInboxRequest;
    const work = async () => {
      const version = state.accountVersion;
      const selectedRef = state.inbox.detailState === "ready" ? state.inbox.selectedConversation?.conversationRef || null : null;
      await loadInbox(null, { background: true });
      if (version !== state.accountVersion) return;
      if (selectedRef && selectedRef === state.inbox.selectedConversation?.conversationRef) {
        await loadConversation(selectedRef, { background: true });
        if (version === state.accountVersion) await reconcileInboxMessages(selectedRef, version);
      }
    };
    const pending = work();
    liveInboxRequest = pending;
    try { await pending; } finally { if (liveInboxRequest === pending) liveInboxRequest = null; }
  }

  async function loadEarlierMessages() {
    const selected = state.inbox.selectedConversation?.conversationRef;
    const cursor = state.inbox.messagePage?.nextCursor;
    if (!selected || !cursor || !state.online || state.inbox.historyState === "loading") return;
    const version = state.accountVersion;
    const request = controller("inbox:history");
    setInbox({ historyState: "loading", historyError: null });
    try {
      const messages = await api.listMessages(selected, { cursor, limit: 30, signal: request.signal });
      if (!currentRequest("inbox:history", request, version) || state.inbox.selectedConversation?.conversationRef !== selected) return;
      if (messages.conversationRef !== selected) throw inboxComposerFailure("INBOX_CONTEXT_SCOPE_MISMATCH", "This conversation could not be verified.", true);
      if (messages.page.hasMore && (!messages.page.nextCursor || messages.page.nextCursor === cursor)) {
        throw inboxComposerFailure("INBOX_HISTORY_CURSOR_STALLED", "Earlier history could not advance. Retry to continue.", true);
      }
      setInbox({
        messages: mergeMessages(messages.messages, state.inbox.messages),
        messagePage: messages.page,
        loadedMessagePages: state.inbox.loadedMessagePages + 1,
        historyCoverage: messages.historyCoverage || state.inbox.historyCoverage,
        historySynchronization: messages.historySynchronization || null,
        historyState: "ready"
      });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("inbox:history", request, version) || state.inbox.selectedConversation?.conversationRef !== selected) return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setInbox({ historyState: "error", historyError: normalized });
    }
  }

  function toggleInboxFilters(open = !state.inbox.filtersOpen) {
    setInbox({ filtersOpen: Boolean(open) });
  }

  function updateInboxFilter(field: Exclude<keyof InboxFilters, "labelIds">, value: string) {
    const next = cloneInboxFilters(state.inbox.filterDraft);
    if (field === "search") next.search = compactInput(value, 80).trimStart();
    else if (field === "channel" && /^[a-z0-9_-]{1,40}$/.test(value)) next.channel = value;
    else if (field === "status" && INBOX_STATUS_FILTERS.has(value as InboxStatusFilter)) next.status = value as InboxStatusFilter;
    else if (field === "sort" && INBOX_SORTS.has(value as InboxSort)) next.sort = value as InboxSort;
    else if (field === "folder" && INBOX_FOLDERS.has(value as InboxFolderId)) next.folder = value as InboxFolderId;
    else return;
    setInbox({ filterDraft: next });
  }

  function toggleInboxLabel(labelId: string, selected: boolean) {
    const normalized = String(labelId || "").trim();
    if (!/^[A-Za-z0-9._:-]{1,80}$/.test(normalized)) return;
    if (state.inbox.labels.length && !state.inbox.labels.some((label) => label.labelId === normalized)) return;
    const labels = new Set(state.inbox.filterDraft.labelIds);
    if (selected) labels.add(normalized);
    else labels.delete(normalized);
    setInbox({ filterDraft: { ...state.inbox.filterDraft, labelIds: [...labels].slice(0, 20) } });
  }

  async function applyInboxFilters() {
    if (state.online === false) {
      setState({ toast: state.ui.app.copy.offlineToast });
      return;
    }
    const filters = cloneInboxFilters(state.inbox.filterDraft);
    filters.search = filters.search.trim();
    rememberInboxDraft();
    abortRequest("inbox:detail");
    abortRequest("inbox:history");
    abortRequest("inbox:live");
    abortRequest("inbox:list:live");
    abortRequest("inbox:compose");
    abortRequest("inbox:draft-review");
    setInbox({
      filters,
      filterDraft: cloneInboxFilters(filters),
      filtersOpen: false,
      listState: "loading",
      conversations: [],
      page: null,
      loadedConversationPages: 0,
      selectedConversation: null,
      contactContext: null,
      messages: [],
      messagePage: null,
      historyCoverage: null,
      liveReconciliation: null,
      liveState: "idle",
      liveError: null,
      loadedMessagePages: 0,
      detailState: "idle",
      historyState: "idle",
      historyError: null,
      contextState: "idle",
      error: null,
      detailError: null,
      contextError: null,
      historySynchronization: null,
      composerMode: "reply",
      composerText: "",
      composerState: "idle",
      composerError: null,
      composerIdempotencyKey: null,
      composerStatus: null,
      ...emptyInboxDraftReviewState()
    });
    await loadInbox();
  }

  async function resetInboxFilters() {
    const filters = cloneInboxFilters();
    if (state.online === false) {
      setInbox({ filterDraft: cloneInboxFilters(filters) });
      setState({ toast: state.ui.app.copy.offlineToast });
      return;
    }
    setInbox({ filterDraft: cloneInboxFilters(filters), filters });
    await applyInboxFilters();
  }

  function setInboxComposerMode(mode: InboxComposerMode) {
    if (mode !== "reply" && mode !== "note") return;
    const capability = mode === "note"
      ? state.inbox.selectedConversation?.capabilities?.internalNote
      : state.inbox.selectedConversation?.capabilities?.reply;
    if (capability?.enabled !== true) return;
    setInbox({
      composerMode: mode,
      composerText: state.inbox.composerText.slice(0, mode === "note" ? 4000 : 1000),
      composerState: "idle",
      composerError: null,
      composerIdempotencyKey: null,
      composerStatus: null
    });
  }

  function updateInboxComposer(value: string) {
    const maximum = state.inbox.composerMode === "note" ? 4000 : 1000;
    const next = String(value || "").replace(/\r\n?/g, "\n").slice(0, maximum);
    if (next === state.inbox.composerText) return;
    setInbox({
      composerText: next,
      composerState: "idle",
      composerError: null,
      composerIdempotencyKey: null,
      composerStatus: null
    });
  }

  function inboxComposerFailure(code: string, message: string, retryable = false): SafeError {
    return { status: 0, code, message, retryable, requiredAction: null, correlationRef: null };
  }

  async function submitInboxComposer() {
    const conversation = state.inbox.selectedConversation;
    if (!conversation || state.inbox.composerState === "loading") return;
    const mode = state.inbox.composerMode;
    const capability = mode === "note" ? conversation.capabilities?.internalNote : conversation.capabilities?.reply;
    if (capability?.enabled !== true) {
      setInbox({
        composerState: "error",
        composerError: inboxComposerFailure(
          capability?.reasonCode || "INBOX_COMPOSER_UNAVAILABLE",
          mode === "note" ? "Internal notes are not available for this account." : "Replies are not available for this conversation."
        )
      });
      return;
    }
    if (state.online === false) {
      setInbox({
        composerState: "error",
        composerError: inboxComposerFailure("INBOX_COMPOSER_OFFLINE", "Reconnect before saving this Inbox draft.", true)
      });
      return;
    }
    const text = state.inbox.composerText.trim();
    const maximum = mode === "note" ? 4000 : 1000;
    if (!text || text.length > maximum) {
      setInbox({
        composerState: "error",
        composerError: inboxComposerFailure("INBOX_COMPOSER_TEXT_REQUIRED", `Enter ${maximum} characters or fewer.`)
      });
      return;
    }
    const version = state.accountVersion;
    const reference = conversation.conversationRef;
    const request = controller("inbox:compose");
    const idempotencyKey = state.inbox.composerIdempotencyKey || createIdempotencyKey(`mobile-inbox-${mode}`);
    setInbox({ composerState: "loading", composerError: null, composerIdempotencyKey: idempotencyKey, composerStatus: null });
    try {
      const result = mode === "note"
        ? await api.createInboxNote(reference, text, idempotencyKey, request.signal)
        : await api.createInboxReplyDraft(reference, text, idempotencyKey, request.signal);
      if (!currentRequest("inbox:compose", request, version) || state.inbox.selectedConversation?.conversationRef !== reference) return;
      setInbox({
        composerText: "",
        composerState: "ready",
        composerError: null,
        composerIdempotencyKey: null,
        composerStatus: result.existing === true
          ? (mode === "note" ? "Note already saved." : "Reply draft already queued.")
          : (mode === "note" ? "Internal note saved." : "Reply draft queued for review.")
      });
      await loadConversation(reference, { background: true });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("inbox:compose", request, version) || state.inbox.selectedConversation?.conversationRef !== reference) return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setInbox({ composerState: "error", composerError: normalized });
    }
  }

  function updateInboxDraftReviewText(value: string) {
    const conversation = state.inbox.selectedConversation;
    if (conversation?.reviewDraft?.status !== "review_required" || conversation.capabilities?.draftEdit?.enabled !== true) return;
    const next = String(value || "").replace(/\r\n?/g, "\n").slice(0, 1000);
    if (next === state.inbox.draftReviewText) return;
    setInbox({
      draftReviewText: next,
      draftReviewDirty: next !== String(conversation.reviewDraft.text || ""),
      draftReviewState: "idle",
      draftReviewError: null,
      draftReviewIdempotencyKey: null,
      draftReviewAction: null,
      draftReviewStatus: null
    });
  }

  function setInboxDraftRejectionReason(reason: InboxDraftRejectionReason) {
    if (!["NEEDS_CHANGES", "UNSAFE_OR_INCORRECT", "WRONG_CONTEXT", "OPERATOR_REJECTED"].includes(reason)) return;
    if (reason === state.inbox.draftReviewReason) return;
    setInbox({
      draftReviewReason: reason,
      draftReviewState: "idle",
      draftReviewError: null,
      draftReviewIdempotencyKey: null,
      draftReviewAction: null,
      draftReviewStatus: null
    });
  }

  function setInboxDraftSendConfirmed(confirmed: boolean) {
    if (state.inbox.selectedConversation?.reviewDraft?.status !== "approved") return;
    setInbox({
      draftSendConfirmed: confirmed === true,
      draftReviewState: "idle",
      draftReviewError: null,
      draftReviewIdempotencyKey: null,
      draftReviewAction: null,
      draftReviewStatus: null
    });
  }

  async function submitInboxDraftReview(action: InboxDraftReviewAction) {
    const conversation = state.inbox.selectedConversation;
    const draft = conversation?.reviewDraft;
    if (!conversation || !draft || state.inbox.draftReviewState === "loading") return;
    const capability = action === "approve"
      ? conversation.capabilities?.draftApprove
      : action === "reject"
        ? conversation.capabilities?.draftReject
        : conversation.capabilities?.send;
    if (capability?.enabled !== true) {
      setInbox({
        draftReviewState: "error",
        draftReviewError: inboxComposerFailure(
          capability?.reasonCode || "INBOX_DRAFT_ACTION_UNAVAILABLE",
          action === "send"
            ? "Sending is not available until this SMS connection passes every safety check."
            : "Draft review is not available for this account."
        )
      });
      return;
    }
    if (state.online === false) {
      setInbox({
        draftReviewState: "error",
        draftReviewError: inboxComposerFailure("INBOX_DRAFT_REVIEW_OFFLINE", "Reconnect before reviewing this draft.", true)
      });
      return;
    }
    if (!draft.messageRef || (action !== "send" && !state.inbox.draftReviewRevision)) {
      setInbox({
        draftReviewState: "error",
        draftReviewError: inboxComposerFailure("INBOX_DRAFT_REFRESH_REQUIRED", "Refresh this conversation before reviewing the draft.", true)
      });
      return;
    }
    const finalText = state.inbox.draftReviewText.trim();
    if (action === "approve" && (!finalText || finalText.length > 1000)) {
      setInbox({
        draftReviewState: "error",
        draftReviewError: inboxComposerFailure("FINAL_TEXT_REQUIRED", "Enter 1,000 characters or fewer before approving this draft.")
      });
      return;
    }
    if (action === "send" && state.inbox.draftSendConfirmed !== true) {
      setInbox({
        draftReviewState: "error",
        draftReviewError: inboxComposerFailure("CONFIRMATION_REQUIRED", "Confirm this exact approved message before sending it.")
      });
      return;
    }

    const version = state.accountVersion;
    const reference = conversation.conversationRef;
    const messageRef = draft.messageRef;
    const request = controller("inbox:draft-review");
    const idempotencyKey = state.inbox.draftReviewAction === action && state.inbox.draftReviewIdempotencyKey
      ? state.inbox.draftReviewIdempotencyKey
      : createIdempotencyKey(`mobile-inbox-draft-${action}`);
    setInbox({
      draftReviewState: "loading",
      draftReviewError: null,
      draftReviewIdempotencyKey: idempotencyKey,
      draftReviewAction: action,
      draftReviewStatus: null
    });
    try {
      const result = action === "approve"
        ? await api.approveInboxDraft(reference, messageRef, finalText, state.inbox.draftReviewRevision!, idempotencyKey, request.signal)
        : action === "reject"
          ? await api.rejectInboxDraft(reference, messageRef, state.inbox.draftReviewReason, state.inbox.draftReviewRevision!, idempotencyKey, request.signal)
          : await api.sendInboxDraft(reference, messageRef, idempotencyKey, request.signal);
      if (!currentRequest("inbox:draft-review", request, version) || state.inbox.selectedConversation?.conversationRef !== reference) return;
      const status = action === "approve"
        ? result.duplicate ? "This draft was already approved." : "Draft approved."
        : action === "reject"
          ? result.duplicate ? "This draft was already rejected." : "Draft rejected."
          : result.sent === true
            ? "Approved message sent."
            : result.deliveryPending || result.accepted === true
            ? "Message accepted for delivery."
            : result.duplicate
              ? "This approved message was already submitted."
              : "Submission recorded. Refresh to check delivery.";
      setInbox({
        draftReviewDirty: false,
        draftReviewState: "ready",
        draftReviewError: null,
        draftReviewIdempotencyKey: null,
        draftReviewAction: null,
        draftReviewStatus: status,
        draftSendConfirmed: false
      });
      await loadConversation(reference, { background: true });
    } catch (error: any) {
      if (error?.code === "REQUEST_CANCELLED") return;
      if (!currentRequest("inbox:draft-review", request, version) || state.inbox.selectedConversation?.conversationRef !== reference) return;
      const normalized = safeError(error);
      if (handleAccountSecurityError(normalized)) return;
      setInbox({ draftReviewState: "error", draftReviewError: normalized });
    }
  }

  function hasUnsavedProfileChanges() {
    return state.companion.dirty
      || state.socialLinks.dirty
      || state.checkins.dirty
      || state.reviewDrafts.dirty
      || state.memoryHydration.dirty;
  }

  function hasUnsentWctekDraft() {
    return Boolean(state.wctek.composer.trim());
  }

  function hasUnsentInboxDraft() {
    return Boolean(state.inbox.composerText.trim() || state.inbox.draftReviewDirty);
  }

  function openProfileView(view: MobileProfileView) {
    if (![
      "public",
      "hub",
      "account",
      "ai-manager",
      "ai-customize",
      "ai-checkins",
      "ai-messaging",
      "ai-memory",
      "ai-connections"
    ].includes(view)) return;
    setState({ activeTab: "profile", profileView: view });
    if (view === "public") void loadPublicProfile();
    if (view === "account" && state.socialLinks.loadState === "idle") void loadSocialLinks();
    if (view === "ai-customize" && state.companion.loadState === "idle") void loadCompanionSettings();
    if (view === "ai-checkins" && state.checkins.loadState === "idle") void loadCheckinSettings();
    if (view === "ai-messaging" && state.reviewDrafts.loadState === "idle") void loadReviewDrafts();
    if (view === "ai-memory" && state.memoryHydration.loadState === "idle") void loadMemoryHydration();
    if (view === "ai-connections" && state.providerConnections.loadState === "idle") void loadProviderConnections();
  }

  function selectTab(tab: MobileTab) {
    if (state.sessionState !== "ready") {
      if (state.sessionState !== "unauthenticated") return;
      setState({ activeTab: tab === "olivia" && MOBILE_GUEST_ENABLED ? "olivia" : "profile" });
      if (MOBILE_GUEST_ENABLED && tab === "olivia" && state.guest.loadState === "idle") void loadGuest();
      return;
    }
    if (tab !== "inbox" && state.inbox.filtersOpen) setInbox({ filtersOpen: false });
    setState({ activeTab: tab, ...(tab === "profile" ? { profileView: "public" as MobileProfileView } : {}) });
    if (tab === "profile") void loadPublicProfile();
    if (tab === "inbox" && state.inbox.listState === "idle") void loadInbox();
    if (tab === "olivia" && state.wctek.loadState === "idle") void loadWctekWorkspace();
  }

  function setOnline(online: boolean) {
    if (!online && state.wctek.sendState === "loading") {
      abortRequest("wctek:send");
      state = {
        ...state,
        wctek: { ...state.wctek, sendState: "error", sendError: wctekOfflineError() }
      };
    }
    if (!online && state.inbox.composerState === "loading") {
      abortRequest("inbox:compose");
      state = {
        ...state,
        inbox: {
          ...state.inbox,
          composerState: "error",
          composerError: inboxComposerFailure("INBOX_COMPOSER_OFFLINE", "Reconnect before saving this Inbox draft.", true)
        }
      };
    }
    if (!online && state.inbox.draftReviewState === "loading") {
      abortRequest("inbox:draft-review");
      state = {
        ...state,
        inbox: {
          ...state.inbox,
          draftReviewState: "error",
          draftReviewError: inboxComposerFailure("INBOX_DRAFT_REVIEW_OFFLINE", "Reconnect before reviewing this draft.", true)
        }
      };
    }
    setState({ online, toast: online ? null : state.ui.app.copy.offlineToast });
  }

  return {
    subscribe(next: Listener) {
      listener = next;
      emit();
    },
    getState: () => state,
    refreshGuestDisplay,
    loadUiConfig,
    loadSession,
    loadPublicProfile,
    resumeSession,
    loadAuthConfig,
    signIn,
    signInWithGoogle,
    verifySecondFactor,
    cancelSecondFactor,
    loadWctekWorkspace,
    loadWctekSessions,
    startNewWctekSession,
    openWctekSession,
    updateWctekComposer,
    sendWctekMessage,
    cancelWctekSend,
    loadCompanionSettings,
    saveCompanionSettings,
    updateCompanionSetting,
    setCompanionPolicyAccepted,
    loadSocialLinks,
    setSocialLinksEnabled,
    addSocialLink,
    updateSocialLink,
    moveSocialLink,
    removeSocialLink,
    saveSocialLinks,
    loadCheckinSettings,
    updateCheckinSetting,
    useDeviceTimezone,
    saveCheckinSettings,
    resetCheckinSettings,
    loadReviewDrafts,
    selectReviewDraft,
    updateReviewDraftText,
    saveReviewDraft,
    markReviewDraft,
    loadMemoryHydration,
    updateMemoryConsent,
    saveMemoryConsent,
    selectMemoryUploadFile,
    setMemoryUploadConsent,
    uploadMemoryFile,
    loadProviderConnections,
    executeProviderAction,
    hasUnsavedProfileChanges,
    hasUnsentWctekDraft,
    openProfileView,
    loadInbox,
    loadInboxMetadata,
    refreshLiveInbox,
    loadEarlierMessages,
    openConversation,
    closeConversation,
    toggleInboxFilters,
    updateInboxFilter,
    toggleInboxLabel,
    applyInboxFilters,
    resetInboxFilters,
    setInboxComposerMode,
    updateInboxComposer,
    submitInboxComposer,
    updateInboxDraftReviewText,
    setInboxDraftRejectionReason,
    setInboxDraftSendConfirmed,
    submitInboxDraftReview,
    selectTab,
    setOnline,
    hasUnsentInboxDraft,
    logout
  };
}

export type MobileStore = ReturnType<typeof createMobileStore>;
